/**
 * @description Página pública para visualização de cards de profissionais
 * @module PractitionerCards
 * @since Novembro 2024
 * @remarks Componente embarcável via iframe para integração com sistemas externos
 */

import { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Stack,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Grid,
  Pagination,
  Skeleton,
  Chip,
  useTheme,
  useMediaQuery,
  Link,
  Avatar,
  Button,
  Divider,
  CardActionArea,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import InstagramIcon from "@mui/icons-material/Instagram";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Info from "@mui/icons-material/Info";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { isMobile } from "react-device-detect";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { DomicilioAddressStep } from "@/pages/schedulemanager/components/creatappointmentdialog/DomicilioAddressStep";
import { toast } from "react-toastify";
import { usePractitionerCards, Practitioner } from "./usePractitionerCards";
import { useSchedule } from "@/services/fhir/schedule/hooks/useSchedule";
import { AppointmentAuthDialog } from "@/components/modals/AppointmentAuthDialog";
import { currentFeatures, endpoints, oids } from "@/configs/Settings";
import { GetPatient, PostAppointment } from "@/services/fhir";
import {
  buildClinicalAutomationTriggerInputFromAppointmentForm,
  buildExternalScheduleAutomationRefs,
  triggerTriageAutomation,
} from "@/services/crm/triggerClinicalAutomation";
import { PostPaymentNotice } from "@/services/fhir/paymentnotice/PostPaymentNotice";
import { PostWhereby } from "@/services/whereby/PostWhereby";
import { GetLocation } from "@/services/fhir/location/GetLocation";
import { GetSlot } from "@/services/fhir/slot/GetSlot";
import { GetSchedule } from "@/services/fhir/schedule/GetSchedule";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";
import { buildCobrancaConsultaNotification } from "@/services/notification/notificationHelpers";
import { notificationService } from "@/services/notification/PostNotification";
import { useFormatter } from "@/utils/useFormatter";
import { useDatetime } from "@/utils/useDatetime";
import { DateFilterType } from "@/utils/interfaces";
import { ViewHeader } from "@/components/schedule/components/ViewHeader";
import dayjs, { Dayjs } from "dayjs";
import { SearchFields } from "@/components/schedule/SearchFields";
import { IFormInput } from "@/pages/schedulingsearch/types";
import { DescriptionOutlined } from "@mui/icons-material";

/**
 * @component PractitionerCard
 * @description Card individual de profissional
 */
interface PractitionerCardProps {
  practitioner: Practitioner;
  onSelect: (practitioner: Practitioner) => void;
  cardSize?: "small" | "medium" | "large";
}

function PractitionerCard({
  practitioner,
  onSelect,
  cardSize = "medium",
}: PractitionerCardProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Define os tamanhos dos cards baseado no parâmetro cardSize
  const cardSizes = {
    small: { xs: "100%", sm: 260, md: 280 },
    medium: { xs: "100%", sm: 320, md: 340 },
    large: { xs: "100%", sm: 380, md: 420 },
  };

  // Define os tamanhos dos avatares baseado no parâmetro cardSize
  const avatarSizes = {
    small: { xs: 80, sm: 100, md: 110 },
    medium: { xs: 100, sm: 120, md: 130 },
    large: { xs: 120, sm: 140, md: 160 },
  };

  // Define os tamanhos dos ícones baseado no parâmetro cardSize
  const iconSizes = {
    small: { xs: 40, sm: 50, md: 55 },
    medium: { xs: 50, sm: 60, md: 65 },
    large: { xs: 60, sm: 70, md: 80 },
  };

  const handleCardClick = () => {
    onSelect(practitioner);
  };

  const handleContactClick = (e: React.MouseEvent) => {
    // Previne que o clique nos botões de contato propague para o card
    e.stopPropagation();
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden",
        transition: "all 0.3s ease-in-out",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        cursor: "pointer",
        maxWidth: cardSizes[cardSize],
        mx: "auto",
        width: "100%",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      <CardActionArea
        onClick={handleCardClick}
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {practitioner.imageUrl ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
              bgcolor: "grey.50",
            }}
          >
            <Avatar
              src={practitioner.imageUrl}
              alt={practitioner.name}
              sx={{
                width: avatarSizes[cardSize],
                height: avatarSizes[cardSize],
                border: "3px solid",
                borderColor: "primary.main",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
              bgcolor: "grey.50",
            }}
          >
            <Avatar
              sx={{
                width: avatarSizes[cardSize],
                height: avatarSizes[cardSize],
                bgcolor: "grey.400",
                border: "3px solid",
                borderColor: "primary.main",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <PersonIcon sx={{ fontSize: iconSizes[cardSize] }} />
            </Avatar>
          </Box>
        )}
        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            p: 2,
          }}
        >
          <Stack spacing={1} flex={1}>
            <Box>
              <Typography
                variant="h6"
                component="h2"
                fontWeight={600}
                color="text.primary"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  lineHeight: 1.3,
                }}
              >
                {practitioner.name}
              </Typography>
              <Chip
                label={practitioner.specialty}
                size="small"
                color="primary"
                sx={{
                  mt: 0.5,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              />
            </Box>

            {practitioner.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.5,
                  flex: 1,
                }}
              >
                {practitioner.description}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>

      {(practitioner.phone || practitioner.instagram) && (
        <Box onClick={handleContactClick} sx={{ px: 2, pb: 1.5 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
          >
            {practitioner.phone && (
              <IconButton
                size="small"
                color="primary"
                component={Link}
                href={`tel:${practitioner.phone.replace(/\D/g, "")}`}
                sx={{
                  bgcolor: "primary",
                  "&:hover": {
                    bgcolor: "primary.main",
                    color: "white",
                  },
                }}
                aria-label={`Ligar para ${practitioner.name}`}
              >
                <PhoneIcon fontSize="small" />
              </IconButton>
            )}

            {practitioner.instagram && (
              <IconButton
                size="small"
                sx={{
                  bgcolor: "#E1306C15",
                  color: "#E1306C",
                  "&:hover": {
                    bgcolor: "#E1306C",
                    color: "white",
                  },
                }}
                component={Link}
                href={`https://instagram.com/${practitioner.instagram.replace(
                  "@",
                  "",
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Instagram de ${practitioner.name}`}
              >
                <InstagramIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
      )}
    </Card>
  );
}

/**
 * @interface ProcessedSlot
 * @description Slot processado para exibição
 */
interface ProcessedSlot {
  id: string;
  time: string;
  endTime: string;
  status: string;
  scheduleId: string;
  scheduleName?: string;
  /**
   * @description Datas completas em ISO (necessárias para criar Appointment)
   */
  start: string;
  end: string;
}

/**
 * @interface DaySchedule
 * @description Agenda de um dia com slots agrupados
 */
interface DaySchedule {
  date: Date;
  dateString: string;
  dayName: string;
  dayNumber: number;
  month: string;
  slots: ProcessedSlot[];
}

/**
 * @description Processa slots da API e agrupa por data
 * @param schedules  - Lista de schedules
 * @param slots      - Lista de slots
 * @param scheduleId - ID do schedule para filtrar (opcional)
 * @param dateFrom   - Início do intervalo a exibir (padrão: hoje)
 * @param dateTo     - Fim do intervalo a exibir (padrão: hoje + 30 dias)
 */
function processScheduleData(
  schedules: fhir5.Schedule[],
  slots: fhir5.Slot[],
  scheduleId?: string,
  dateFrom?: Date,
  dateTo?: Date,
): DaySchedule[] {
  // Cria mapa de schedules para lookup
  const scheduleMap = new Map<string, fhir5.Schedule>();
  schedules.forEach((schedule) => {
    if (schedule.id) {
      scheduleMap.set(schedule.id, schedule);
    }
  });

  // Filtra apenas slots disponíveis (free) e opcionalmente por scheduleId
  let freeSlots = slots.filter((slot) => slot.status === "free");

  // Se scheduleId foi fornecido, filtra apenas os slots desse schedule
  if (scheduleId) {
    freeSlots = freeSlots.filter((slot) => {
      const slotScheduleRef = slot.schedule?.reference;
      const slotScheduleId = slotScheduleRef?.split("/")[1];
      return slotScheduleId === scheduleId;
    });
  }

  // Filtra slots que já passaram (considera data e hora)
  const now = dayjs();
  freeSlots = freeSlots.filter((slot) => {
    if (!slot.start) return false;
    // Compara a data/hora de início do slot com o momento atual
    return dayjs(slot.start).isAfter(now);
  });

  // Agrupa slots por data
  const slotsByDate = new Map<string, ProcessedSlot[]>();

  freeSlots.forEach((slot) => {
    if (!slot.start || !slot.id) return;

    const startDate = new Date(slot.start);
    const dateKey = startDate.toISOString().split("T")[0];

    // Extrai ID do schedule da referência
    const scheduleRef = slot.schedule?.reference;
    const scheduleId = scheduleRef?.split("/")[1] || "";
    const schedule = scheduleMap.get(scheduleId);

    const processedSlot: ProcessedSlot = {
      id: slot.id,
      time: startDate.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      endTime: slot.end
        ? new Date(slot.end).toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "",
      status: slot.status || "free",
      scheduleId,
      scheduleName: schedule?.name,
      // Mantém os timestamps originais do Slot para a criação do Appointment
      start: slot.start,
      end: slot.end || "",
    };

    const existingSlots = slotsByDate.get(dateKey) || [];
    existingSlots.push(processedSlot);
    slotsByDate.set(dateKey, existingSlots);
  });

  // Define o intervalo de datas a gerar.
  // dateFrom: início do período (padrão: hoje)
  // dateTo:   fim do período (padrão: hoje + 30 dias)
  const startDay = dateFrom ? new Date(dateFrom) : new Date();
  startDay.setHours(0, 0, 0, 0);

  const endDay = dateTo ? new Date(dateTo) : new Date(startDay);
  if (!dateTo) endDay.setDate(startDay.getDate() + 29); // 30 dias inclusive
  endDay.setHours(23, 59, 59, 999);

  // Gera um dia por iteração dentro do intervalo
  const result: DaySchedule[] = [];
  const current = new Date(startDay);

  while (current <= endDay) {
    const dateString = current.toISOString().split("T")[0];

    const daySlots = slotsByDate.get(dateString) || [];
    // Ordena slots por horário
    daySlots.sort((a, b) => a.time.localeCompare(b.time));

    result.push({
      date: new Date(current),
      dateString,
      dayName: current.toLocaleDateString("pt-BR", { weekday: "long" }),
      dayNumber: current.getDate(),
      month: current.toLocaleDateString("pt-BR", { month: "short" }),
      slots: daySlots,
    });

    current.setDate(current.getDate() + 1);
  }

  return result;
}

/**
 * @component ScheduleLoadingState
 * @description Estado de carregamento para a agenda
 */
function ScheduleLoadingState() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        gap: 2,
      }}
    >
      <CircularProgress size={40} />
      <Typography color="text.secondary">Carregando horários...</Typography>
    </Box>
  );
}

/**
 * @component NoScheduleState
 * @description Estado quando não há agendas cadastradas
 */
function NoScheduleState() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        gap: 2,
      }}
    >
      <CalendarTodayIcon sx={{ fontSize: 48, color: "grey.400" }} />
      <Typography variant="h6" color="text.secondary" textAlign="center">
        Nenhuma agenda disponível
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        Este profissional ainda não possui horários configurados
      </Typography>
    </Box>
  );
}

/**
 * @description Extrai informações de extensões do Schedule
 */
function getScheduleExtensions(schedule: fhir5.Schedule) {
  const slotDuration = schedule.extension?.find(
    (ext) =>
      ext.url ===
      "https://fluxmed.com.br/fhir/StructureDefinition/schedule-slot-duration",
  )?.valueDuration;

  const anticipationPeriod = schedule.extension?.find(
    (ext) =>
      ext.url ===
      "https://fluxmed.com.br/fhir/StructureDefinition/schedule-slot-anticipation-period",
  )?.valueDuration;

  const serviceCost = schedule.extension?.find(
    (ext) =>
      ext.url ===
      "https://fluxmed.com.br/fhir/StructureDefinition/service-cost",
  )?.valueDecimal;

  const returnDays = schedule.extension?.find(
    (ext) =>
      ext.url ===
      "https://fluxmed.com.br/fhir/StructureDefinition/schedule-return-days",
  )?.valueDuration;

  return {
    slotDuration,
    anticipationPeriod,
    serviceCost,
    returnDays,
  };
}

/**
 * @description Extrai informações dos atores do Schedule
 */
function getScheduleActors(schedule: fhir5.Schedule) {
  const location = schedule.actor?.find((actor) =>
    actor.reference?.startsWith("Location/"),
  );
  const healthcareService = schedule.actor?.find((actor) =>
    actor.reference?.startsWith("HealthcareService/"),
  );

  return {
    location: location?.display || "Não informado",
    healthcareService: healthcareService?.display || "Não informado",
  };
}

/**
 * Mapeamento de serviceModel → rótulo legível.
 * Centralizado aqui para manter consistência em todo o arquivo.
 */
const SERVICE_MODEL_LABELS: Record<string, string> = {
  virtualService: "Teleatendimento",
  chatService: "Chat",
  domicilioService: "Atendimento a Domicílio",
  physicalLocation: "Presencial",
};

/**
 * @description Lê o canal de atendimento da extension attendance-channel.
 * Valores possíveis: "teleatendimento" | "chat" | "domicilio"
 */
function getAttendanceChannelFromLocation(
  location: fhir5.Location | undefined,
): string | undefined {
  return (
    location?.extension?.find((ext) =>
      ext.url?.includes("attendance-channel"),
    ) as any
  )?.valueCode;
}

/**
 * @description Verifica se uma Location corresponde ao serviceModel solicitado.
 * Mesma lógica de usePractitionerCards.tsx — mantida sincronizada.
 * - virtualService:   virtual-service=true e canal NÃO é chat (chat pode repetir flag virtual por engano).
 * - chatService:      attendance-channel=chat
 * - domicilioService: attendance-channel=domicilio
 * - physicalLocation: sem virtual-service e sem canal especial
 */
function locationMatchesModel(
  location: fhir5.Location | undefined,
  model: string,
): boolean {
  if (!location) return false;
  const channel = getAttendanceChannelFromLocation(location);
  const isVirtual = !!location.extension?.find(
    (ext) =>
      ext.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service" &&
      ext.valueBoolean === true,
  );

  switch (model) {
    case "virtualService":
      // Agenda externa: teleatendimento não deve listar Locations exclusivas de chat.
      if (channel === "chat") return false;
      return isVirtual;
    case "chatService":
      return channel === "chat";
    case "domicilioService":
      return channel === "domicilio";
    case "physicalLocation":
      return !isVirtual && channel !== "chat" && channel !== "domicilio";
    default:
      return true;
  }
}

/**
 * @component PractitionerSchedule
 * @description Visualização dos horários de um profissional
 */
interface PractitionerScheduleProps {
  practitioner: Practitioner;
  onBack: () => void;
  /** Schedules pré-carregados para evitar nova busca */
  preloadedSchedules?: fhir5.Schedule[];
  /** Locations pré-carregadas (scheduleId -> Location) para evitar nova busca */
  preloadedLocations?: Map<string, fhir5.Location>;
  /**
   * Filtro de modelo de atendimento.
   * Valores: "virtualService" | "chatService" | "domicilioService" | "physicalLocation"
   */
  serviceModel?: string;
  /** Filtro de modelo de atendimento para filtrar schedules (virtualService/physicalLocation) */
  advancePayment?: boolean;
}

function PractitionerSchedule({
  practitioner,
  onBack,
  preloadedSchedules,
  preloadedLocations,
  serviceModel,
  advancePayment,
}: PractitionerScheduleProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { extractNumericValue } = useFormatter;
  const [searchParams] = useSearchParams();
  const organizationFromUrl = searchParams.get("organization") || undefined;
  // Para compatibilidade: se o parâmetro estiver na URL, ele tem prioridade.
  const advancePaymentFromUrl = searchParams.get("advancePayment");
  const isAdvancePaymentEnabled =
    advancePaymentFromUrl === null
      ? !!advancePayment
      : advancePaymentFromUrl === "true";
  // Organization será obtida da Location do schedule, não do context
  const [organization, setOrganization] = useState<
    fhir5.Organization | undefined
  >(undefined);

  // Location completa para exibir o endereço (quando um schedule específico está selecionado)
  const [location, setLocation] = useState<fhir5.Location | undefined>(
    undefined,
  );
  // Mapa de scheduleId -> Location para exibir endereços nos cards de seleção
  const [locationsMap, setLocationsMap] = useState<Map<string, fhir5.Location>>(
    new Map(),
  );
  // Controle para saber quando o filtro por modelo de atendimento (serviceModel)
  // já pode ser aplicado com segurança (ou seja, quando as Locations necessárias
  // já foram carregadas). Isso evita "piscar" de agendas na tela.
  const [isServiceModelFilterReady, setIsServiceModelFilterReady] =
    useState<boolean>(() => !serviceModel);

  const [selectedSchedule, setSelectedSchedule] =
    useState<fhir5.Schedule | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<ProcessedSlot | null>(null);
  // Estado para quando o profissional não apresenta horários - permite selecionar apenas o dia
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [creatingAppointment, setCreatingAppointment] = useState(false);
  const [showAppointmentSuccessAlert, setShowAppointmentSuccessAlert] =
    useState(false);
  // Estado para controlar exibição do componente de sucesso
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  // Estado do fluxo de pagamento antecipado (quando aplicável)
  const [advancePaymentLink, setAdvancePaymentLink] = useState<string | null>(
    null,
  );
  const [advancePaymentChargeSent, setAdvancePaymentChargeSent] =
    useState(false);
  const [advancePaymentChargeError, setAdvancePaymentChargeError] = useState<
    string | null
  >(null);
  const [advancePaymentPhoneMissing, setAdvancePaymentPhoneMissing] =
    useState(false);
  // No app vet, precisamos do pet selecionado para criar o Appointment (patientActor = Pet).
  const selectedVetPetRef = useRef<{ id: string } | null>(null);
  // Evita loops de atualização ao buscar/montar Locations para os schedules filtrados.
  // Armazena uma "assinatura" do conjunto de schedules atual (ids) + modo (preloaded/API).
  const lastLocationsFetchKeyRef = useRef<string>("");

  // Contador de fetch de slots — evita race condition ao navegar rapidamente entre semanas.
  // Quando um novo fetch inicia, o contador avança; respostas de fetches antigos são descartadas.
  const slotsFetchIdRef = useRef(0);

  // Controla o dialog de coleta de endereço para agendamentos do tipo "domicílio"
  const [domicilioAddressDialogOpen, setDomicilioAddressDialogOpen] =
    useState(false);

  // Salva slot e schedule em refs ao entrar no fluxo de domicílio.
  // Necessário porque o AppointmentAuthDialog chama onClose() após onSuccess(),
  // e onClose() limpa selectedSlot/selectedSchedule via setSelectedSlot(null).
  // Os refs preservam os valores para uso em createPublicAppointment().
  const domicilioSlotRef = useRef<ProcessedSlot | null>(null);
  const domicilioScheduleRef = useRef<fhir5.Schedule | null>(null);

  // Form isolado para o passo de endereço domiciliar.
  // Contém os campos "cpf" (para busca do paciente) e "domicilioAddress".
  // O CPF é preenchido a partir do localStorage após a autenticação do paciente.
  const {
    control: addressControl,
    setValue: addressSetValue,
    trigger: addressTrigger,
    watch: addressWatch,
    reset: addressReset,
  } = useForm({ defaultValues: { cpf: "", domicilioAddress: undefined } });

  // Estados para filtros de data
  const [dateFilter, setDateFilter] = useState<Dayjs>(dayjs());
  const [filterType, setFilterType] = useState<DateFilterType>("week");
  const [slots, setSlots] = useState<fhir5.Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const { formatDateToISO, formatDateTimeToISO, isDateFilterTypeDay } =
    useDatetime;

  // Verifica se Location é virtual (teleatendimento) - mesma lógica de usePractitionerCards
  const isVirtualLocation = (loc: fhir5.Location | undefined): boolean => {
    if (!loc) return false;
    const ext = loc.extension?.find(
      (e) =>
        e.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service",
    );
    return ext?.valueBoolean === true;
  };

  // Verifica se a agenda deve ser considerada para exposição pública.
  // Regra:
  // - Se NÃO existir a extensão schedule-exposure => mantém compatibilidade (agenda considerada).
  // - Se existir a extensão => só considera quando valueBoolean === true.
  const hasExposureEnabled = (schedule: fhir5.Schedule): boolean => {
    const extensions = schedule.extension;
    if (!extensions || extensions.length === 0) {
      // Sem extensão configurada: mantém comportamento antigo (agenda visível)
      return true;
    }

    const exposureExtension = extensions.find(
      (ext) =>
        ext.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/schedule-exposure",
    );

    // Sem a extensão específica: também mantém compatibilidade (agenda visível)
    if (!exposureExtension) return true;

    // Com extensão: só considera quando valueBoolean é true
    return exposureExtension.valueBoolean === true;
  };

  // Filtra preloaded schedules por exposição E por serviceModel
  // - Exposição: apenas agendas habilitadas OU sem extensão configurada.
  // - serviceModel: usa locationMatchesModel para suportar todos os 4 tipos.
  const filteredPreloadedSchedules = useMemo(() => {
    if (!preloadedSchedules?.length || !serviceModel) return preloadedSchedules;
    if (!preloadedLocations?.size) return preloadedSchedules;
    return preloadedSchedules.filter((schedule) => {
      // Primeiro, aplica filtro de exposição
      if (!hasExposureEnabled(schedule)) return false;

      const location = schedule.id
        ? preloadedLocations.get(schedule.id)
        : undefined;
      return locationMatchesModel(location, serviceModel);
    });
  }, [preloadedSchedules, preloadedLocations, serviceModel]);

  // Filtra preloaded locations para incluir apenas as dos schedules filtrados
  const filteredPreloadedLocations = useMemo(() => {
    if (!filteredPreloadedSchedules?.length || !preloadedLocations?.size)
      return preloadedLocations;
    const filtered = new Map<string, fhir5.Location>();
    filteredPreloadedSchedules.forEach((s) => {
      if (s.id) {
        const loc = preloadedLocations.get(s.id);
        if (loc) filtered.set(s.id, loc);
      }
    });
    return filtered;
  }, [filteredPreloadedSchedules, preloadedLocations]);

  const effectiveSchedules = filteredPreloadedSchedules ?? preloadedSchedules;
  const effectiveLocations = filteredPreloadedLocations ?? preloadedLocations;

  // Busca schedules via API apenas quando NÃO há dados pré-carregados
  // enabled=false evita chamada desnecessária quando temos preloaded
  const { data, loading, error, fetchSchedules } = useSchedule({
    practitioner: `Practitioner/${practitioner.id}`,
    includeSlot: false,
    active: true,
    enabled: !effectiveSchedules,
  });

  // Se há schedules pré-carregados, usa eles diretamente (sem refetch)
  const schedulesData = effectiveSchedules
    ? {
        schedules: effectiveSchedules,
        slots: [] as fhir5.Slot[],
        loading: false,
        error: null as Error | null,
      }
    : {
        schedules: data.schedules,
        slots: data.slots,
        loading,
        error,
      };

  // Aplica o filtro de exposição também para os schedules vindos via hook useSchedule.
  // Quando o parâmetro serviceModel estiver definido, aplica também o filtro de
  // modelo de atendimento (virtualService = teleconsulta, physicalLocation = presencial),
  // usando a Location associada ao schedule (locationsMap ou effectiveLocations).
  const exposureFilteredSchedules = useMemo(() => {
    // Sempre aplica o filtro de exposição.
    // O filtro por serviceModel (virtual/presencial) só é aplicado
    // quando já existe Location carregada (effectiveLocations ou locationsMap),
    // para evitar recarregamentos e garantir consistência.
    const canFilterByServiceModel =
      !!serviceModel &&
      ((effectiveLocations && effectiveLocations.size > 0) ||
        locationsMap.size > 0);

    return schedulesData.schedules.filter((schedule) => {
      // Primeiro, aplica o filtro de exposição (extensão schedule-exposure).
      if (!hasExposureEnabled(schedule)) {
        return false;
      }

      // Se ainda não podemos aplicar o filtro por modelo de atendimento,
      // mantém o schedule (apenas com filtro de exposição).
      if (!canFilterByServiceModel) {
        return true;
      }

      // Tenta obter a Location associada ao schedule.
      // - Para dados pré-carregados: effectiveLocations (scheduleId -> Location).
      // - Para dados carregados via API: locationsMap (scheduleId -> Location).
      const scheduleId = schedule.id ?? "";
      const scheduleLocation =
        locationsMap.get(scheduleId) ?? effectiveLocations?.get(scheduleId);

      // Se não encontramos Location para este schedule, mantemos o registro,
      // pois o filtro por modelo de atendimento não pode ser avaliado.
      if (!scheduleLocation) {
        return true;
      }

      // Aplica filtro pelo modelo de atendimento (suporta todos os 4 tipos).
      return locationMatchesModel(scheduleLocation, serviceModel);
    });
  }, [schedulesData.schedules, serviceModel, locationsMap, effectiveLocations]);

  // Busca dados apenas quando não há schedules pré-carregados
  useEffect(() => {
    if (!effectiveSchedules) {
      // Atenção: não podemos limpar o localStorage aqui, pois esta página
      // roda em contexto externo (iframe) e compartilha o storage com a
      // aplicação principal. Limpar tudo derruba a sessão do usuário.
      fetchSchedules();
    }
  }, [fetchSchedules, effectiveSchedules]);

  // Busca todas as Locations de todos os schedules quando os schedules são carregados
  // Se Locations pré-carregadas foram fornecidas, usa elas diretamente
  useEffect(() => {
    async function fetchAllLocations() {
      if (
        schedulesData.loading ||
        schedulesData.error ||
        exposureFilteredSchedules.length === 0
      ) {
        return;
      }

      // Verifica se existem Locations pré-carregadas (map não vazio).
      const hasPreloadedLocations =
        !!effectiveLocations && effectiveLocations.size > 0;

      // Monta uma chave determinística do conjunto atual de schedules filtrados.
      // Isso evita reexecutar o efeito em loop quando `setLocationsMap` muda estado
      // e dispara novo render.
      const scheduleIdsKey = exposureFilteredSchedules
        .map((s) => s.id)
        .filter(Boolean)
        .sort()
        .join("|");
      const fetchModeKey = hasPreloadedLocations ? "preloaded" : "api";
      const nextFetchKey = `${fetchModeKey}:${scheduleIdsKey}`;
      if (lastLocationsFetchKeyRef.current === nextFetchKey) {
        return;
      }
      lastLocationsFetchKeyRef.current = nextFetchKey;

      // Quando não há Locations pré-carregadas (ou seja, vamos depender apenas
      // de buscas via API) e o mapa de Locations já foi preenchido, evitamos
      // refazer as requisições desnecessariamente para impedir loop infinito.
      if (!hasPreloadedLocations && locationsMap.size > 0) {
        return;
      }

      // Se há Locations pré-carregadas, usa elas diretamente
      if (hasPreloadedLocations) {
        const newLocationsMap = new Map<string, fhir5.Location>();
        exposureFilteredSchedules.forEach((schedule) => {
          if (schedule.id) {
            const loc = effectiveLocations.get(schedule.id);
            if (loc) newLocationsMap.set(schedule.id, loc);
          }
        });
        setLocationsMap(newLocationsMap);
        // Assim que o mapa de Locations estiver pronto e houver serviceModel,
        // o filtro por modelo de atendimento pode ser aplicado sem "piscar".
        if (serviceModel) {
          setIsServiceModelFilterReady(true);
        }
        return;
      }

      // Caso contrário, busca Locations normalmente
      const newLocationsMap = new Map<string, fhir5.Location>();

      // Busca Location de cada schedule
      const locationPromises = exposureFilteredSchedules.map(
        async (schedule) => {
          if (!schedule.id) return;

          // Extrai o Location actor do schedule
          const locationActor = schedule.actor?.find((a) =>
            a.reference?.startsWith("Location/"),
          );

          if (!locationActor?.reference) {
            return;
          }

          // Extrai o ID da Location da referência (formato: "Location/ID")
          const locationId = locationActor.reference.split("/")[1];

          if (!locationId) {
            return;
          }

          try {
            // Busca Location
            const locationBundle = await GetLocation({
              ids: locationId,
              includeOrganization: false, // Não precisa da Organization aqui
            });

            // Extrai a Location do bundle
            const locationEntry = locationBundle?.entry?.find(
              (entry: any) => entry.resource?.resourceType === "Location",
            );

            if (locationEntry?.resource && schedule.id) {
              newLocationsMap.set(
                schedule.id,
                locationEntry.resource as fhir5.Location,
              );
            }
          } catch (error) {
            console.error(
              `Erro ao buscar Location para schedule ${schedule.id}:`,
              error,
            );
          }
        },
      );

      // Aguarda todas as buscas
      await Promise.all(locationPromises);

      // Atualiza o mapa de Locations
      setLocationsMap(newLocationsMap);
      // Quando todas as Locations forem carregadas via API e houver serviceModel,
      // habilita o filtro por modelo de atendimento.
      if (serviceModel) {
        setIsServiceModelFilterReady(true);
      }
    }

    fetchAllLocations();
  }, [
    exposureFilteredSchedules,
    schedulesData.loading,
    schedulesData.error,
    effectiveLocations,
    serviceModel,
  ]);

  // Seleciona automaticamente o schedule se houver apenas um
  useEffect(() => {
    if (
      !schedulesData.loading &&
      !schedulesData.error &&
      (!serviceModel || isServiceModelFilterReady) &&
      exposureFilteredSchedules.length === 1 &&
      !selectedSchedule
    ) {
      setSelectedSchedule(exposureFilteredSchedules[0]);
    }
  }, [
    exposureFilteredSchedules,
    schedulesData.loading,
    schedulesData.error,
    selectedSchedule,
    serviceModel,
    isServiceModelFilterReady,
  ]);

  // Busca Location e Organization quando um schedule é selecionado
  // Se Locations pré-carregadas estão disponíveis, usa elas diretamente
  useEffect(() => {
    async function fetchLocationAndOrganization() {
      if (!selectedSchedule) {
        setOrganization(undefined);
        setLocation(undefined);
        return;
      }

      // Se há Locations pré-carregadas, usa a Location do schedule selecionado
      if (effectiveLocations && selectedSchedule.id) {
        const preloadedLocation = effectiveLocations.get(selectedSchedule.id);
        if (preloadedLocation) {
          setLocation(preloadedLocation);
          // A Location pré-carregada não inclui Organization completa
          // Se necessário, pode ser obtida via managingOrganization.reference
          // NOTE: Esse id é necessário para permitir `PaymentNotice` quando `serviceCost > 0`.
          // Evitamos chamada extra: derivamos o id a partir de `Location.managingOrganization.reference`.
          const managingOrgRef =
            preloadedLocation.managingOrganization?.reference;
          const managingOrgId = managingOrgRef?.split("/")?.[1];
          // Fallback final: se não houver managingOrganization na Location,
          // usa a Organization vinda da URL (ex.: "123" ou "Organization/123").
          const urlOrgId =
            organizationFromUrl?.split("/")?.[1] ?? organizationFromUrl;
          setOrganization(
            managingOrgId
              ? ({
                  resourceType: "Organization",
                  id: managingOrgId,
                } as fhir5.Organization)
              : urlOrgId
                ? ({
                    resourceType: "Organization",
                    id: urlOrgId,
                  } as fhir5.Organization)
                : undefined,
          );
          return;
        }
      }

      // Caso contrário, busca normalmente (fallback para quando não há pré-carregamento)
      // Extrai o Location actor do schedule
      const locationActor = selectedSchedule.actor?.find((a) =>
        a.reference?.startsWith("Location/"),
      );

      if (!locationActor?.reference) {
        setOrganization(undefined);
        setLocation(undefined);
        return;
      }

      // Extrai o ID da Location da referência (formato: "Location/ID")
      const locationId = locationActor.reference.split("/")[1];

      if (!locationId) {
        setOrganization(undefined);
        setLocation(undefined);
        return;
      }

      try {
        // Busca Location com _include=Location:organization para obter a Organization relacionada
        const locationBundle = await GetLocation({
          ids: locationId,
          includeOrganization: true,
        });

        // Extrai a Location do bundle
        const locationEntry = locationBundle?.entry?.find(
          (entry: any) => entry.resource?.resourceType === "Location",
        );

        if (locationEntry?.resource) {
          setLocation(locationEntry.resource as fhir5.Location);
        } else {
          setLocation(undefined);
        }

        // Extrai a Organization do bundle (vem incluída quando usa _include)
        const organizationEntry = locationBundle?.entry?.find(
          (entry: any) => entry.resource?.resourceType === "Organization",
        );

        if (organizationEntry?.resource) {
          setOrganization(organizationEntry.resource as fhir5.Organization);
        } else {
          // Fallback: quando o _include não retorna a Organization completa,
          // tentamos obter ao menos o id via `Location.managingOrganization.reference`.
          const fetchedLocation = locationEntry?.resource as
            | fhir5.Location
            | undefined;
          const managingOrgRef =
            fetchedLocation?.managingOrganization?.reference;
          const managingOrgId = managingOrgRef?.split("/")?.[1];
          // Fallback final: se o _include não retornou Organization e a Location não
          // tiver managingOrganization, usa a Organization vinda da URL.
          const urlOrgId =
            organizationFromUrl?.split("/")?.[1] ?? organizationFromUrl;
          setOrganization(
            managingOrgId
              ? ({
                  resourceType: "Organization",
                  id: managingOrgId,
                } as fhir5.Organization)
              : urlOrgId
                ? ({
                    resourceType: "Organization",
                    id: urlOrgId,
                  } as fhir5.Organization)
                : undefined,
          );
        }
      } catch (error) {
        console.error("Erro ao buscar Location e Organization:", error);
        setOrganization(undefined);
        setLocation(undefined);
      }
    }

    fetchLocationAndOrganization();
  }, [selectedSchedule, effectiveLocations]);

  // Busca slots quando um schedule é selecionado e uma data é escolhida.
  // Usa slotsFetchIdRef para descartar respostas de fetches antigos (race condition).
  useEffect(() => {
    // Avança o contador: qualquer resposta com ID menor que o atual é obsoleta
    const fetchId = ++slotsFetchIdRef.current;

    async function fetchSlots() {
      if (!selectedSchedule?.id) {
        setSlots([]);
        setLoadingSlots(false);
        return;
      }

      setLoadingSlots(true);
      try {
        // Calcula startDate e endDate para a query de slots.
        //
        // Regras:
        // - Nunca buscar slots que já passaram (startDate >= agora quando o período já começou).
        // - O endDate deve cobrir os 30 dias exibidos pelo processSlots, evitando janelas
        //   curtas que deixam dias sem dados (ex: fim da semana corrente em uma quinta-feira).
        let startDate: string;
        let endDate: string;

        if (isDateFilterTypeDay(filterType)) {
          // Filtro de dia único
          const isToday = dayjs().isSame(dateFilter, "day");
          startDate = isToday
            ? formatDateTimeToISO(dayjs()) // hora atual para não exibir passados
            : formatDateToISO(dateFilter);
          endDate = formatDateToISO(dateFilter);
        } else if (filterType === "week") {
          // Filtro semanal: busca do início efetivo da semana até o fim da semana.
          // Se a semana já começou (passado), parte do momento atual para não buscar
          // slots já expirados.
          const weekStart = dateFilter.startOf("week");
          const now = dayjs();

          if (weekStart.isBefore(now, "day")) {
            // Semana em andamento — parte do horário atual
            startDate = formatDateTimeToISO(now);
          } else if (now.isSame(weekStart, "day")) {
            // Semana começa hoje — usa hora atual
            startDate = formatDateTimeToISO(now);
          } else {
            // Semana futura — começa do início do dia
            startDate = formatDateToISO(weekStart);
          }
          // endDate = fim da semana selecionada (consistente com o display)
          endDate = formatDateToISO(dateFilter.endOf("week"));
        } else {
          // Filtro de mês
          const monthStart = dateFilter.startOf("month");
          const now = dayjs();

          if (monthStart.isBefore(now, "day")) {
            // Mês já em andamento — começa do momento atual
            startDate = formatDateTimeToISO(now);
          } else if (now.isSame(monthStart, "day")) {
            startDate = formatDateTimeToISO(now);
          } else {
            startDate = formatDateToISO(monthStart);
          }
          endDate = formatDateToISO(dateFilter.endOf("month"));
        }

        // Busca slots usando GetSlot
        const slotsBundle = await GetSlot({
          schedule: selectedSchedule.id,
          status: ["free"],
          sort: "start",
          startDate,
          endDate,
        });

        // Extrai os slots do bundle
        const slotsArray =
          slotsBundle?.entry
            ?.filter(
              (entry: any): entry is fhir5.BundleEntry<fhir5.Slot> =>
                entry.resource?.resourceType === "Slot",
            )
            .map((entry: any) => entry.resource!)
            .filter(
              (resource: any): resource is fhir5.Slot => resource !== undefined,
            ) || [];

        // Descarta resposta se um fetch mais recente já foi iniciado
        if (fetchId !== slotsFetchIdRef.current) return;

        setSlots(slotsArray);
      } catch (error) {
        if (fetchId !== slotsFetchIdRef.current) return;
        console.error("Erro ao buscar slots:", error);
        setSlots([]);
      } finally {
        if (fetchId !== slotsFetchIdRef.current) return;
        setLoadingSlots(false);
      }
    }

    fetchSlots();
  }, [
    selectedSchedule?.id,
    dateFilter,
    filterType,
    formatDateToISO,
    formatDateTimeToISO,
    isDateFilterTypeDay,
  ]);

  // Abre o dialog de confirmação automaticamente quando um slot é selecionado
  useEffect(() => {
    if (selectedSlot) {
      setAuthDialogOpen(true);
    }
  }, [selectedSlot]);

  // Calcula o intervalo de dias a exibir de acordo com filterType e dateFilter.
  // Nunca exibe dias anteriores a hoje (evita mostrar datas já passadas).
  const { displayFrom, displayTo } = useMemo(() => {
    const todayStart = dayjs().startOf("day");

    if (filterType === "day") {
      // Dia único selecionado
      const day = dateFilter.startOf("day");
      const from = day.isBefore(todayStart) ? todayStart : day;
      return {
        displayFrom: from.toDate(),
        displayTo: dateFilter.endOf("day").toDate(),
      };
    }

    if (filterType === "week") {
      // Semana: exibe do início da semana (ou hoje, se a semana já começou) até o fim da semana
      const weekStart = dateFilter.startOf("week");
      const weekEnd = dateFilter.endOf("week");
      const from = weekStart.isBefore(todayStart) ? todayStart : weekStart;
      return { displayFrom: from.toDate(), displayTo: weekEnd.toDate() };
    }

    // Mês: exibe do início do mês (ou hoje, se o mês já começou) até o fim do mês
    const monthStart = dateFilter.startOf("month");
    const monthEnd = dateFilter.endOf("month");
    const from = monthStart.isBefore(todayStart) ? todayStart : monthStart;
    return { displayFrom: from.toDate(), displayTo: monthEnd.toDate() };
  }, [dateFilter, filterType]);

  // Processa os dados da API - filtra por schedule selecionado e intervalo de datas
  const schedule = useMemo(
    () =>
      processScheduleData(
        exposureFilteredSchedules,
        slots,
        selectedSchedule?.id,
        displayFrom,
        displayTo,
      ),
    [
      exposureFilteredSchedules,
      slots,
      selectedSchedule?.id,
      displayFrom,
      displayTo,
    ],
  );

  // Quando um dia é selecionado (modo sem apresentação de horários),
  // busca o último slot disponível daquele dia
  useEffect(() => {
    if (selectedDay && !practitioner.schedulePresentation) {
      // Busca o último slot do dia selecionado
      const daySchedule = schedule.find(
        (day) =>
          day.date.toDateString() === selectedDay.toDateString() &&
          day.slots.length > 0,
      );

      if (daySchedule && daySchedule.slots.length > 0) {
        // Seleciona o último slot disponível do dia
        const lastSlot = daySchedule.slots[daySchedule.slots.length - 1];
        setSelectedSlot(lastSlot);
      }
    }
  }, [selectedDay, schedule, practitioner.schedulePresentation]);

  // Verifica se tem algum slot disponível
  const hasAnySlots = slots.length > 0;

  /**
   * Retorna o rótulo do tipo de atendimento de uma Location.
   * Cobre todos os 4 tipos: virtual, chat, domicílio e presencial.
   */
  const getAttendanceTypeLabel = (
    location: fhir5.Location | undefined,
  ): string | null => {
    if (!location) return null;
    const channel = getAttendanceChannelFromLocation(location);
    const isVirtual = isVirtualLocation(location);

    if (channel === "chat") return "Chat";
    if (channel === "domicilio") return "Atendimento a Domicílio";
    if (isVirtual) return "Teleatendimento";
    return null; // presencial — usa endereço normal
  };

  // Função para verificar se uma Location é de teleconsulta (virtual-service=true)
  const isTeleconsultation = (
    location: fhir5.Location | undefined,
  ): boolean => {
    if (!location) return false;
    return isVirtualLocation(location);
  };

  // Função para formatar o endereço da Location
  const formatLocationAddress = (location: fhir5.Location | undefined) => {
    // Se for virtual-like (tele, chat), retorna apenas o rótulo do tipo
    const typeLabel = getAttendanceTypeLabel(location);
    if (typeLabel) return typeLabel;

    if (!location?.address) {
      return "Endereço não informado";
    }

    const address = location.address;
    const parts: string[] = [];

    // Adiciona as linhas do endereço (rua, número, complemento)
    if (address.line && address.line.length > 0) {
      parts.push(address.line.join(", "));
    }

    // Adiciona cidade, estado e CEP
    const cityStateZip = [address.city, address.state, address.postalCode]
      .filter((el) => el)
      .join(", ");

    if (cityStateZip) {
      parts.push(cityStateZip);
    }

    return parts.length > 0 ? parts.join(" - ") : "Endereço não informado";
  };

  // Função para voltar à seleção de schedules
  const handleBackToSchedules = () => {
    setSelectedSchedule(null);
    setSelectedSlot(null);
    setShowAppointmentSuccessAlert(false);
    setShowSuccessDialog(false);
    // Reseta os filtros de data para hoje e visualização semanal
    setDateFilter(dayjs());
    setFilterType("week");
    setSlots([]);
  };

  /**
   * @description Reinicia o fluxo de agendamento para permitir marcar outro horário
   * Limpa todas as seleções e volta ao estado inicial
   */
  const handleResetAppointmentFlow = () => {
    setShowSuccessDialog(false);
    setShowAppointmentSuccessAlert(false);
    setSelectedSlot(null);
    setSelectedDay(null);
    setSelectedSchedule(null);
    selectedVetPetRef.current = null;
    setAdvancePaymentLink(null);
    setAdvancePaymentChargeSent(false);
    setAdvancePaymentChargeError(null);
    setAdvancePaymentPhoneMissing(false);
    // Reseta os filtros de data para hoje e visualização semanal
    setDateFilter(dayjs());
    setFilterType("week");
    setSlots([]);
  };

  const getPortalBaseUrl = () => {
    // Segue o padrão já usado em ScheduleIntegrator (dev/local -> dev, senão app).
    const appEnvironment =
      window.location.origin?.includes("dev") ||
      window.location.origin?.includes("localhost")
        ? "dev"
        : "app";
    const portalType = currentFeatures.defaultApp ? "fluxmed" : "fluxvet";
    return `https://${appEnvironment}.${portalType}.com.br`;
  };

  const getPatientPortalUrls = () => {
    const base = getPortalBaseUrl();
    return {
      signin: `${base}/portal/#/signin`,
      recover: `${base}/portal/#/recover`,
      signup: `${base}/portal/#/signup`,
    };
  };

  /**
   * @description Extrai actors do Schedule (Location/HealthcareService)
   * @remarks No fluxo público não temos o form do CreateAppointmentDialog, então montamos diretamente
   * os actors necessários para o `auxiliarData.Appointment`.
   */
  const getScheduleActorRefs = (schedule: fhir5.Schedule | null) => {
    const location = schedule?.actor?.find((a) =>
      a.reference?.startsWith("Location/"),
    );
    const healthcareService = schedule?.actor?.find((a) =>
      a.reference?.startsWith("HealthcareService/"),
    );
    const practitioner = schedule?.actor?.find((a) =>
      a.reference?.startsWith("Practitioner/"),
    );

    return {
      locationActor: {
        reference: location?.reference || "",
        display: location?.display || "Local não informado",
      },
      healthCareServiceActor: {
        reference: healthcareService?.reference || "",
        display: healthcareService?.display || "Serviço não informado",
      },
      practitionerActor: {
        reference: practitioner?.reference || "",
        display: practitioner?.display || "Profissional",
      },
    };
  };

  /**
   * @description Cria PaymentNotice quando há custo no serviço
   * Baseado na função createPaymentNotice do useAppointmentSubmission.ts
   */
  const createPaymentNotice = async (
    appointmentId: string,
    slotId: string,
    serviceCost: number,
    patientActor: { reference: string; display: string },
    professionalActor: { reference: string; display: string },
    locationActor: { reference: string; display: string },
  ) => {
    if (!serviceCost || serviceCost <= 0 || !organization?.id) {
      return;
    }

    const dataPaymentNotice: any = {
      resourceType: "PaymentNotice",
      status: "active",
      extension: [
        {
          url: "http://fluxmed.com.br/fhir/StructureDefinition/AppointmentReference",
          valueReference: {
            reference: `Appointment/${appointmentId}`,
          },
        },
        {
          url: "http://fluxmed.com.br/fhir/StructureDefinition/OriginalServiceCost",
          valueDecimal: serviceCost,
        },
      ],
      identifier: [
        {
          system: "https://fluxmed.com.br/fhir/identifier/organization",
          value: `Organization/${organization.id}`,
        },
        {
          system: "https://fluxmed.com.br/fhir/identifier/location",
          value: locationActor.reference,
        },
      ],
      response: {
        reference: patientActor.reference,
      },
      request: {
        reference: `Slot/${slotId}`,
      },
      created: dayjs().format(),
      reporter: { reference: professionalActor.reference },
      recipient: { reference: `Organization/${organization.id}` },
      amount: {
        value: `${serviceCost}`,
        currency: "BRL",
      },
    };

    await PostPaymentNotice(dataPaymentNotice);
  };

  /**
   * @description Cria o Appointment usando os dados disponíveis na tela pública
   *
   * Regras importantes:
   * - defaultApp=true (humano): patientActor.reference precisa conter \"-CPF\" para `auxiliarData.Appointment` gerar Patient?identifier
   * - defaultApp=false (vet): patientActor.reference deve ser \"Patient/<petId>\" (id direto)
   * - Se houver serviceCost no schedule, cria PaymentNotice após criar o appointment
   */
  const createPublicAppointment = async () => {
    // Para o fluxo de domicílio, o AppointmentAuthDialog chama onClose() depois de onSuccess(),
    // limpando selectedSlot/selectedSchedule. Usamos refs como fallback nesses casos.
    const slotToUse = selectedSlot ?? domicilioSlotRef.current;
    const scheduleToUse = selectedSchedule ?? domicilioScheduleRef.current;

    if (!slotToUse || !scheduleToUse) {
      toast.error("Selecione um horário para continuar.");
      return;
    }

    const cpf = (localStorage.getItem("username") || "").replace(/\D/g, "");
    if (!cpf) {
      toast.error("Não foi possível identificar o usuário autenticado.");
      return;
    }

    // Para o app vet, o Appointment deve ser criado para o pet.
    const petId = selectedVetPetRef.current?.id;
    if (!currentFeatures.defaultApp && !petId) {
      toast.error("Selecione um pet para continuar.");
      return;
    }

    const { locationActor, healthCareServiceActor, practitionerActor } =
      getScheduleActorRefs(scheduleToUse);
    if (!locationActor.reference || !healthCareServiceActor.reference) {
      toast.error(
        "Agenda sem informações de local/serviço. Não foi possível agendar.",
      );
      return;
    }

    // Location do schedule selecionado (estado ou mapa pré-carregado) para Organization.
    const resolvedLocation =
      location ?? locationsMap.get(scheduleToUse?.id ?? "") ?? undefined;

    const externalScheduleRefs = buildExternalScheduleAutomationRefs({
      schedule: scheduleToUse,
      location: resolvedLocation,
      organizationFhirIdFallback: organization?.id,
    });

    // Extrai o custo do serviço do schedule
    // valueDecimal já vem como número do getScheduleExtensions
    const extensions = getScheduleExtensions(scheduleToUse);
    const serviceCost =
      extensions.serviceCost !== undefined ? extensions.serviceCost : 0;
    const hasServiceCost = serviceCost > 0;

    // Prepara o patientActor baseado no tipo de app
    const patientActor = currentFeatures.defaultApp
      ? {
          reference: `Patient/${oids.cpf}-${cpf}`,
          display: "Paciente",
        }
      : {
          reference: `Patient/${petId}`,
          display: "Pet",
        };

    // Profissional do Schedule (fonte de verdade na agenda externa).
    const professionalActor = practitionerActor.reference
      ? {
          reference: practitionerActor.reference,
          display: practitionerActor.display || practitioner.name,
        }
      : {
          reference: `Practitioner/${practitioner.id}`,
          display: practitioner.name,
        };

    setCreatingAppointment(true);
    try {
      // Lê o endereço coletado no DomicilioAddressStep (disponível quando serviceModel=domicilioService)
      const domicilioAddressData = addressWatch("domicilioAddress");

      // Teleatendimento e Chat: cria sala Whereby antes do Appointment.
      // Mesmo fluxo de useAppointmentSubmission / ConfirmAppointmentDialog na agenda interna.
      let virtualServicePayload:
        | { virtualService: Array<{ addressUrl: string; sessionKey: string }> }
        | undefined;
      const attendanceChannel =
        getAttendanceChannelFromLocation(resolvedLocation);
      const needsWherebyRoom =
        attendanceChannel === "chat" || isTeleconsultation(resolvedLocation);

      if (needsWherebyRoom) {
        try {
          const wherebyData = {
            endDate: dayjs(slotToUse.end).format("YYYY-MM-DDTHH:mm:ss-03:00"),
            roomNamePrefix: `${endpoints?.AppName}-whereby`,
            roomNamePattern: "human-short",
          };
          const wherebyResponse = await PostWhereby(wherebyData);
          virtualServicePayload = {
            virtualService: [
              {
                addressUrl: wherebyResponse.roomUrl,
                sessionKey: wherebyResponse.meetingId || "",
              },
            ],
          };
        } catch (wherebyError) {
          // Mantém o agendamento mesmo sem sala virtual (comportamento da agenda interna).
          console.error(
            "Erro ao criar sala Whereby na agenda externa:",
            wherebyError,
          );
        }
      }

      // Cria o appointment
      const createdAppointment = await PostAppointment({
        cpf,
        typeService: "ROUTINE", // Fluxo público não escolhe tipo; usamos um padrão simples
        schedule: scheduleToUse.id,
        slotInfo: {
          id: slotToUse.id,
          start: slotToUse.start,
          end: slotToUse.end,
        },
        patientActor,
        professionalActor,
        healthCareServiceActor,
        locationActor,
        // Define se é gratuito baseado na presença de custo
        appointmentIsFree: !hasServiceCost,
        serviceCost: hasServiceCost ? serviceCost.toString() : "",
        // Status "proposed" para agendamentos públicos (aguardando confirmação da clínica)
        appointmentStatus: "proposed",
        // Inclui endereço no Appointment somente para atendimento a domicílio
        ...(serviceModel === "domicilioService" && domicilioAddressData
          ? { domicilioAddress: domicilioAddressData }
          : {}),
        ...(virtualServicePayload ?? {}),
      });

      // Se houver custo, cria o PaymentNotice
      if (hasServiceCost && createdAppointment?.id && organization?.id) {
        try {
          await createPaymentNotice(
            createdAppointment.id,
            slotToUse.id,
            serviceCost,
            patientActor,
            professionalActor,
            locationActor,
          );
        } catch (paymentError) {
          console.error("Erro ao criar PaymentNotice:", paymentError);
          // Não bloqueia o sucesso do appointment se o PaymentNotice falhar
          toast.warning(
            "Agendamento criado, mas houve um problema ao gerar o pagamento. Entre em contato com a clínica.",
          );
        }
      }

      // Automação de triagem (best-effort): referências derivadas do Schedule + Location.
      try {
        if (externalScheduleRefs) {
          const triageTriggerInput =
            buildClinicalAutomationTriggerInputFromAppointmentForm({
              organizationId: externalScheduleRefs.organizationFhirId,
              professionalActorReference:
                externalScheduleRefs.practitionerReference,
              patientActorReference: patientActor.reference,
              patientDisplayName: patientActor.display,
              patientCpf: cpf,
              appointmentId: createdAppointment.id,
              appointmentStartAtIso:
                createdAppointment.start ?? slotToUse.start,
              requesterReference: externalScheduleRefs.requesterReference,
            });
          if (triageTriggerInput) {
            // Aguarda envio antes de liberar a UI — evita sair da página sem disparar.
            await triggerTriageAutomation(triageTriggerInput);
          }
        }
      } catch (automationError) {
        console.error("[automationTrigger] triagem:", automationError);
      }

      // Pagamento antecipado (apenas no fluxo humano): gera link e tenta enviar cobrança via WhatsApp.
      // Regra: só tenta se advancePayment=true E agenda tem custo.
      // Mantém compatibilidade: sem parâmetro, o fluxo atual continua igual.
      if (
        hasServiceCost &&
        isAdvancePaymentEnabled &&
        currentFeatures.defaultApp
      ) {
        setAdvancePaymentChargeError(null);
        setAdvancePaymentChargeSent(false);
        setAdvancePaymentPhoneMissing(false);
        try {
          const patientResponse = await GetPatient({
            identifier: cpf,
            authType: "application",
          });
          const patientResource = patientResponse?.data as
            | fhir5.Patient
            | undefined;
          const patientName =
            patientResource?.name?.find(
              (el: any) => el.use === "usual" || el.use === "official",
            )?.text || "Paciente";
          const patientPhone =
            patientResource?.telecom?.find((t: any) => t.system === "phone")
              ?.value || "";

          const paymentLink = await marketplaceService.sendPaymentMessage({
            slotReference: `Slot/${slotToUse.id}`,
            patientName,
            healthcareDisplay: healthCareServiceActor.display || "",
            practitionerName: practitioner.name || "",
            phoneNumber: patientPhone || "",
          });
          setAdvancePaymentLink(paymentLink || null);

          if (!patientPhone) {
            // Sem telefone: ainda mostramos o link na UI, mas não tentamos WhatsApp.
            setAdvancePaymentPhoneMissing(true);
          } else {
            await notificationService.sendNotification(
              buildCobrancaConsultaNotification({
                cpf,
                nomePaciente: patientName,
                tipoConsulta: healthCareServiceActor.display || "",
                nomeProfissional: practitioner.name || "",
                linkPagamento: paymentLink,
                organizationId: organization?.id,
              }),
              { organizationId: organization?.id },
            );
            setAdvancePaymentChargeSent(true);
          }
        } catch (err) {
          console.error("Erro ao enviar cobrança antecipada:", err);
          setAdvancePaymentChargeError(
            "Não foi possível enviar a cobrança automaticamente. Você ainda pode pagar pelo link abaixo.",
          );
        }
      }
      // Mostra componente de sucesso
      setShowSuccessDialog(true);
      // Reseta seleção para evitar duplo clique/reenvio
      setSelectedSlot(null);
      selectedVetPetRef.current = null;
      domicilioSlotRef.current = null;
      domicilioScheduleRef.current = null;
    } catch (error) {
      console.error("Erro ao criar Appointment:", error);
      toast.error("Erro ao criar agendamento. Tente novamente.");
    } finally {
      setCreatingAppointment(false);
    }
  };

  // Renderiza a lista de schedules para seleção
  const renderScheduleSelection = () => {
    // Enquanto o filtro por serviceModel ainda não estiver pronto,
    // evita renderizar as agendas para não mostrar uma lista "bruta"
    // que será filtrada 1s depois (efeito de piscar).
    const shouldHideSchedulesForServiceModel =
      !!serviceModel && !isServiceModelFilterReady;

    if (shouldHideSchedulesForServiceModel) {
      return (
        <Box sx={{ py: 2 }}>
          <ScheduleLoadingState />
        </Box>
      );
    }

    return (
      <>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Selecione uma agenda
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Escolha a agenda que melhor atende suas necessidades
        </Typography>

        <Grid container spacing={2} mt={1}>
          {exposureFilteredSchedules.map((scheduleItem) => {
            const extensions = getScheduleExtensions(scheduleItem);
            const actors = getScheduleActors(scheduleItem);
            // Obtém a Location associada a este schedule (quando disponível)
            const scheduleLocation = scheduleItem.id
              ? locationsMap.get(scheduleItem.id)
              : undefined;
            // Determina se este schedule deve ser exibido como "sem endereço físico".
            // - virtualService e chatService: não mostram endereço (link/texto simples).
            // - domicilioService: mostra endereço específico do paciente — não é isTele.
            // - Usa o rótulo da Location quando disponível; cai de volta no serviceModel.
            const attendanceLabel =
              getAttendanceTypeLabel(scheduleLocation) ??
              (serviceModel ? SERVICE_MODEL_LABELS[serviceModel] : null);
            const isTele =
              serviceModel === "virtualService" ||
              serviceModel === "chatService" ||
              (isTeleconsultation(scheduleLocation) &&
                getAttendanceChannelFromLocation(scheduleLocation) !==
                  "domicilio");

            return (
              <Grid item xs={12} sm={6} md={4} key={scheduleItem.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease-in-out",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 2.5,
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Stack spacing={2} flex={1}>
                      {/* Nome da agenda */}
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        fontSize="1.1rem"
                      >
                        {scheduleItem.name || "Agenda sem nome"}
                      </Typography>

                      {/* Descrição opcional (Schedule.comment) */}
                      {typeof scheduleItem.comment === "string" &&
                        scheduleItem.comment.trim() && (
                          <Tooltip title={scheduleItem.comment.trim()}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: 1.5,
                              }}
                            >
                              {scheduleItem.comment.trim()}
                            </Typography>
                          </Tooltip>
                        )}

                      {/* Detalhes da agenda - 3 informações */}
                      <Stack spacing={1.5} flex={1}>
                        {/* Local */}
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <LocationOnIcon
                            sx={{
                              fontSize: 18,
                              color: "primary.main",
                              mt: 0.2,
                            }}
                          />
                          <Box flex={1}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              fontSize="0.7rem"
                            >
                              Local
                            </Typography>

                            {/* Para atendimentos virtuais (tele/chat), exibe apenas o rótulo do tipo */}
                            {isTele ? (
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                fontSize="0.875rem"
                              >
                                {attendanceLabel ?? "Teleconsulta"}
                              </Typography>
                            ) : (
                              <>
                                {/* Para atendimentos presenciais, exibe o nome do local */}
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  fontSize="0.875rem"
                                >
                                  {actors.location}
                                </Typography>
                                {/* Exibe endereço formatado quando Location estiver disponível */}
                                {scheduleLocation && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    fontSize="0.75rem"
                                    sx={{ mt: 0.5 }}
                                  >
                                    {formatLocationAddress(scheduleLocation)}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        </Stack>

                        {/* Valor */}
                        {extensions.serviceCost !== undefined && (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <AttachMoneyIcon
                              sx={{
                                fontSize: 18,
                                color: "primary.main",
                                mt: 0.2,
                              }}
                            />
                            <Box flex={1}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                fontSize="0.7rem"
                              >
                                Valor
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                fontSize="0.875rem"
                              >
                                R$ {extensions.serviceCost.toFixed(2)}
                              </Typography>
                            </Box>
                          </Stack>
                        )}

                        {/* Período de planejamento */}
                        {scheduleItem.planningHorizon && (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <CalendarTodayIcon
                              sx={{
                                fontSize: 18,
                                color: "primary.main",
                                mt: 0.2,
                              }}
                            />
                            <Box flex={1}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                fontSize="0.7rem"
                              >
                                Período
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                fontSize="0.875rem"
                              >
                                {new Date(
                                  scheduleItem.planningHorizon.start || "",
                                ).toLocaleDateString("pt-BR", {
                                  day: "2-digit",
                                  month: "short",
                                })}{" "}
                                até{" "}
                                {new Date(
                                  scheduleItem.planningHorizon.end || "",
                                ).toLocaleDateString("pt-BR", {
                                  day: "2-digit",
                                  month: "short",
                                })}
                              </Typography>
                            </Box>
                          </Stack>
                        )}
                      </Stack>

                      {/* Botão para ver horários */}
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => setSelectedSchedule(scheduleItem)}
                        sx={{
                          borderRadius: 2,
                          py: 1.2,
                          fontWeight: 600,
                          textTransform: "none",
                          fontSize: "0.9rem",
                        }}
                      >
                        Ver horários
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </>
    );
  };

  return (
    <Stack spacing={3} width="100%" maxWidth={800} mx="auto">
      {/* Header com botão voltar */}
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            mb: 2,
            color: "text.secondary",
            "&:hover": {
              bgcolor: "action.hover",
            },
          }}
        >
          Voltar para profissionais
        </Button>

        {/* Alert informativo sobre agendamento criado como "proposed" */}
        {showAppointmentSuccessAlert && (
          <Alert
            severity="info"
            onClose={() => setShowAppointmentSuccessAlert(false)}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Agendamento criado com sucesso!
            </Typography>
            <Typography variant="body2">
              Seu agendamento foi marcado como <strong>proposto</strong> e está
              aguardando confirmação da clínica. Você receberá uma confirmação
              via WhatsApp ou pode acessar a plataforma para acompanhar o status
              do agendamento.
            </Typography>
          </Alert>
        )}

        {/* Informações do profissional */}
        <Card
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            p={2.5}
            alignItems={{ xs: "center", sm: "flex-start" }}
          >
            {practitioner.imageUrl ? (
              <Avatar
                src={practitioner.imageUrl}
                alt={practitioner.name}
                sx={{
                  width: { xs: 100, sm: 120, md: 140 },
                  height: { xs: 100, sm: 120, md: 140 },
                  border: "3px solid",
                  borderColor: "primary.main",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: { xs: 100, sm: 120, md: 140 },
                  height: { xs: 100, sm: 120, md: 140 },
                  bgcolor: "grey.400",
                  border: "3px solid",
                  borderColor: "primary.main",
                }}
              >
                <PersonIcon sx={{ fontSize: { xs: 50, sm: 60, md: 70 } }} />
              </Avatar>
            )}

            <Stack
              spacing={1}
              textAlign={{ xs: "center", sm: "left" }}
              flex={1}
            >
              <Typography variant="h5" fontWeight={600}>
                {practitioner.name}
              </Typography>
              <Chip
                label={practitioner.specialty}
                size="small"
                color="primary"
                sx={{ alignSelf: { xs: "center", sm: "flex-start" } }}
              />
              {practitioner.description && (
                <Typography variant="body2" color="text.secondary">
                  {practitioner.description}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Card>
      </Box>

      {/* Estado de carregamento */}
      {loading && <ScheduleLoadingState />}

      {/* Estado de erro */}
      {error && !loading && (
        <Card
          sx={{ borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
        >
          <CardContent>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="error" gutterBottom>
                Erro ao carregar agendas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {error.message}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => fetchSchedules()}
                sx={{ mt: 2 }}
              >
                Tentar novamente
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Estado sem agendas */}
      {!schedulesData.loading &&
        !schedulesData.error &&
        (!serviceModel || isServiceModelFilterReady) &&
        exposureFilteredSchedules.length === 0 && (
          <Card
            sx={{ borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
          >
            <CardContent>
              <NoScheduleState />
            </CardContent>
          </Card>
        )}

      {/* Seleção de Schedule ou Horários */}
      {!schedulesData.loading &&
        !schedulesData.error &&
        (!serviceModel || isServiceModelFilterReady) &&
        exposureFilteredSchedules.length > 0 && (
          <Card
            sx={{ borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
          >
            <CardContent>
              {!selectedSchedule ? (
                // Mostra lista de schedules para seleção
                renderScheduleSelection()
              ) : (
                // Mostra horários do schedule selecionado
                <Stack spacing={2}>
                  {/* Botão para voltar à seleção de schedules - só mostra se houver mais de 1 schedule */}
                  {exposureFilteredSchedules.length > 1 && (
                    <Button
                      startIcon={<ArrowBackIcon />}
                      onClick={handleBackToSchedules}
                      sx={{
                        alignSelf: "flex-start",
                        color: "text.secondary",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      Voltar para agendas
                    </Button>
                  )}

                  {/* Filtros de data */}
                  <ViewHeader
                    dateFilter={dateFilter}
                    filterType={filterType}
                    setDateFilter={setDateFilter}
                    setFilterType={setFilterType}
                    minDate={dayjs().startOf("day")} // Mínimo é hoje
                  />

                  {/* Informações do schedule selecionado */}
                  <Box mt={2}>
                    {/* Detalhes resumidos do schedule */}
                    <Stack spacing={1} mb={2}>
                      {(() => {
                        const extensions =
                          getScheduleExtensions(selectedSchedule);
                        const actors = getScheduleActors(selectedSchedule);

                        return (
                          <>
                            {typeof selectedSchedule.comment === "string" &&
                              selectedSchedule.comment.trim() && (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="flex-start"
                                >
                                  <DescriptionOutlined
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                      mt: 0.2,
                                    }}
                                  />

                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                  >
                                    {selectedSchedule.comment.trim()}
                                  </Typography>
                                </Stack>
                              )}

                            {/* Exibe o endereço da Location se disponível */}
                            {location && (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                              >
                                <LocationOnIcon
                                  sx={{
                                    fontSize: 18,
                                    color: "text.secondary",
                                    mt: 0.2,
                                  }}
                                />

                                <Typography
                                  variant="body1"
                                  color="text.secondary"
                                >
                                  {formatLocationAddress(location)}
                                </Typography>
                              </Stack>
                            )}

                            {extensions.serviceCost !== undefined && (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <AttachMoneyIcon
                                  sx={{ fontSize: 18, color: "text.secondary" }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  R$ {extensions.serviceCost.toFixed(2)}
                                </Typography>
                              </Stack>
                            )}

                            {selectedSchedule.planningHorizon && (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <CalendarTodayIcon
                                  sx={{ fontSize: 18, color: "text.secondary" }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {new Date(
                                    selectedSchedule.planningHorizon.start ||
                                      "",
                                  ).toLocaleDateString("pt-BR", {
                                    day: "2-digit",
                                    month: "short",
                                  })}{" "}
                                  até{" "}
                                  {new Date(
                                    selectedSchedule.planningHorizon.end || "",
                                  ).toLocaleDateString("pt-BR", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </Typography>
                              </Stack>
                            )}
                          </>
                        );
                      })()}
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Verifica se há slots disponíveis para este schedule */}
                  {loadingSlots ? (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: "center",
                        bgcolor: "grey.50",
                        borderRadius: 2,
                      }}
                    >
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary" mt={2}>
                        Carregando horários disponíveis...
                      </Typography>
                    </Box>
                  ) : !hasAnySlots ? (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: "center",
                        bgcolor: "grey.50",
                        borderRadius: 2,
                      }}
                    >
                      <CalendarTodayIcon
                        sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                      />
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        gutterBottom
                      >
                        Nenhum horário disponível
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Não há horários livres para o período selecionado
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Renderização condicional baseado em schedulePresentation */}
                      {practitioner.schedulePresentation === false ? (
                        // ===== MODO SEM HORÁRIOS: Apenas seleção de dia =====
                        <Box>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            mb={2}
                          >
                            <CalendarTodayIcon color="primary" />
                            <Typography variant="h6" fontWeight={600}>
                              Dias disponíveis
                            </Typography>
                          </Stack>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={2}
                          >
                            Selecione um dia para solicitar o agendamento. A
                            clínica confirmará o horário específico com você.
                          </Typography>

                          {schedule.some((day) => day.slots.length > 0) ? (
                            <Grid container spacing={2}>
                              {schedule
                                ?.filter((day) => day.slots.length > 0)
                                ?.map((day) => (
                                  <Grid
                                    item
                                    xs={6}
                                    sm={4}
                                    md={3}
                                    key={day.dateString}
                                  >
                                    <Box
                                      onClick={() => setSelectedDay(day.date)}
                                      sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        textAlign: "center",
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: "2px solid",
                                        borderColor:
                                          selectedDay?.toDateString() ===
                                          day.date.toDateString()
                                            ? "primary.main"
                                            : "grey.200",
                                        bgcolor:
                                          selectedDay?.toDateString() ===
                                          day.date.toDateString()
                                            ? "primary"
                                            : "grey.50",
                                        "&:hover": {
                                          borderColor: "primary.main",
                                          bgcolor: "primary",
                                        },
                                      }}
                                    >
                                      <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        color="primary"
                                      >
                                        {day.dayNumber}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        color="text.secondary"
                                        sx={{ textTransform: "capitalize" }}
                                      >
                                        {day.dayName}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        color="text.secondary"
                                      >
                                        {day.month}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ))}
                            </Grid>
                          ) : (
                            <Box
                              sx={{
                                py: 4,
                                textAlign: "center",
                                bgcolor: "grey.50",
                                borderRadius: 2,
                              }}
                            >
                              <Typography color="text.secondary">
                                Nenhum dia disponível para o período selecionado
                              </Typography>
                            </Box>
                          )}

                          {selectedDay && selectedSlot && (
                            <>
                              <Divider sx={{ mt: 3 }} />
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                justifyContent="space-between"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                spacing={2}
                                mt={2}
                              >
                                <Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Data selecionada:
                                  </Typography>
                                  <Typography variant="body1" fontWeight={600}>
                                    {selectedDay.toLocaleDateString("pt-BR", {
                                      weekday: "long",
                                      day: "2-digit",
                                      month: "long",
                                      year: "numeric",
                                    })}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mt={0.5}
                                  >
                                    A clínica confirmará o horário específico
                                    após sua solicitação
                                  </Typography>
                                </Box>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size={isMobileScreen ? "medium" : "large"}
                                  onClick={() => setAuthDialogOpen(true)}
                                  disabled={creatingAppointment}
                                  sx={{
                                    borderRadius: 2,
                                    px: 4,
                                    fontWeight: 600,
                                  }}
                                >
                                  {creatingAppointment
                                    ? "Solicitando..."
                                    : "Solicitar agendamento"}
                                </Button>
                              </Stack>
                            </>
                          )}
                        </Box>
                      ) : (
                        // ===== MODO COM HORÁRIOS: Exibição completa de slots =====
                        <Box>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            mb={2}
                          >
                            <AccessTimeIcon color="primary" />
                            <Typography variant="h6" fontWeight={600}>
                              Horários disponíveis
                            </Typography>
                          </Stack>

                          {/* Exibe todos os slots do período filtrado, agrupados por data */}
                          {schedule.some((day) => day.slots.length > 0) ? (
                            <Stack spacing={3}>
                              {schedule
                                .filter((day) => day.slots.length > 0)
                                .map((day) => (
                                  <Box key={day.dateString}>
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={600}
                                      color="text.secondary"
                                      mb={1.5}
                                    >
                                      {day.date.toLocaleDateString("pt-BR", {
                                        weekday: "long",
                                        day: "2-digit",
                                        month: "long",
                                      })}
                                    </Typography>
                                    <Grid container spacing={1}>
                                      {day.slots.map((slot) => (
                                        <Grid
                                          item
                                          xs={4}
                                          sm={3}
                                          md={2}
                                          key={slot.id}
                                        >
                                          <Box
                                            onClick={() =>
                                              setSelectedSlot(slot)
                                            }
                                            sx={{
                                              p: 1.5,
                                              borderRadius: 2,
                                              textAlign: "center",
                                              cursor: "pointer",
                                              transition: "all 0.2s",
                                              border: "2px solid",
                                              borderColor:
                                                selectedSlot?.id === slot.id
                                                  ? "primary.main"
                                                  : "grey.200",
                                              bgcolor:
                                                selectedSlot?.id === slot.id
                                                  ? "primary"
                                                  : "transparent",
                                              color:
                                                selectedSlot?.id === slot.id
                                                  ? "primary"
                                                  : "text.primary",
                                              opacity:
                                                selectedSlot?.id === slot.id
                                                  ? 1
                                                  : 0.8,
                                              "&:hover": {
                                                borderColor: "primary.main",
                                                opacity: 1,
                                                bgcolor:
                                                  selectedSlot?.id === slot.id
                                                    ? "primary"
                                                    : "action.hover",
                                              },
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              fontWeight={600}
                                            >
                                              {slot.time}
                                            </Typography>
                                            {slot.endTime && (
                                              <Typography
                                                variant="caption"
                                                display="block"
                                                color="text.secondary"
                                              >
                                                {slot.endTime}
                                              </Typography>
                                            )}
                                          </Box>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Box>
                                ))}
                            </Stack>
                          ) : (
                            <Box
                              sx={{
                                py: 4,
                                textAlign: "center",
                                bgcolor: "grey.50",
                                borderRadius: 2,
                              }}
                            >
                              <Typography color="text.secondary">
                                Nenhum horário disponível para o período
                                selecionado
                              </Typography>
                            </Box>
                          )}

                          {selectedSlot && (
                            <>
                              <Divider sx={{ mt: 3 }} />
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                justifyContent="space-between"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                spacing={2}
                                mt={2}
                              >
                                <Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Horário selecionado:
                                  </Typography>
                                  <Typography variant="body1" fontWeight={600}>
                                    {/* Encontra a data do slot selecionado */}
                                    {(() => {
                                      const slotDay = schedule.find((day) =>
                                        day.slots.some(
                                          (s) => s.id === selectedSlot.id,
                                        ),
                                      );
                                      return slotDay
                                        ? `${slotDay.date.toLocaleDateString(
                                            "pt-BR",
                                            {
                                              weekday: "long",
                                              day: "2-digit",
                                              month: "long",
                                            },
                                          )} às ${selectedSlot.time}`
                                        : `${selectedSlot.time}`;
                                    })()}
                                    {selectedSlot.endTime &&
                                      ` - ${selectedSlot.endTime}`}
                                  </Typography>
                                </Box>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size={isMobileScreen ? "medium" : "large"}
                                  onClick={() => setAuthDialogOpen(true)}
                                  disabled={creatingAppointment}
                                  sx={{
                                    borderRadius: 2,
                                    px: 4,
                                    fontWeight: 600,
                                  }}
                                >
                                  {creatingAppointment
                                    ? "Agendando..."
                                    : "Confirmar agendamento"}
                                </Button>
                              </Stack>
                            </>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        )}

      {/* Dialog de loading durante criação do agendamento */}
      <Dialog
        open={creatingAppointment}
        disableEscapeKeyDown
        disableAutoFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 3,
            minWidth: { xs: "90%", sm: 400 },
          },
        }}
      >
        <DialogContent>
          <Stack
            spacing={3}
            alignItems="center"
            justifyContent="center"
            sx={{ py: 2 }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" fontWeight={600} textAlign="center">
              Criando agendamento...
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Aguarde enquanto processamos sua solicitação
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Dialog de sucesso após criação do agendamento */}

      {showSuccessDialog && (
        <Dialog
          open={showSuccessDialog}
          onClose={() => {
            setShowSuccessDialog(false);
            handleResetAppointmentFlow();
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogContent>
            <Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
              {/* Ícone de sucesso */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "success",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 50, color: "success.main" }} />
              </Box>

              {/* Título */}
              <Typography variant="h5" fontWeight={600} textAlign="center">
                Agendamento solicitado com sucesso!
              </Typography>

              {/* Mensagem informativa */}
              {/* <Alert severity="info" sx={{ width: "100%" }}>
                <Typography variant="body2">
                  Seu agendamento foi solicitado com sucesso e está aguardando
                  confirmação da clínica. Você receberá uma confirmação via
                  WhatsApp ou pode acessar a plataforma para acompanhar o status
                  do agendamento.
                </Typography>
              </Alert> */}

              {/* Pagamento antecipado (quando aplicável) */}
              {isAdvancePaymentEnabled &&
                (advancePaymentLink ||
                  advancePaymentChargeSent ||
                  advancePaymentChargeError ||
                  advancePaymentPhoneMissing) && (
                  <Stack spacing={1.5} width="100%">
                    {advancePaymentChargeSent && (
                      <Alert
                        severity="success"
                        icon={<WhatsAppIcon />}
                        sx={{ width: "100%" }}
                      >
                        <Typography variant="body2">
                          Link de pagamento enviado para o número de WhatsApp
                          cadastrado.
                        </Typography>
                      </Alert>
                    )}
                    {advancePaymentPhoneMissing && (
                      <Alert severity="warning" sx={{ width: "100%" }}>
                        <Typography variant="body2">
                          Não encontramos um telefone cadastrado para envio via
                          WhatsApp. Use o link abaixo para concluir o pagamento.
                        </Typography>
                      </Alert>
                    )}
                    {advancePaymentChargeError && (
                      <Alert severity="warning" sx={{ width: "100%" }}>
                        <Typography variant="body2">
                          {advancePaymentChargeError}
                        </Typography>
                      </Alert>
                    )}

                    {advancePaymentLink && (
                      <Card
                        variant="outlined"
                        sx={{ borderRadius: 3, overflow: "hidden" }}
                      >
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              Pagar agora
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Conclua o pagamento para dar prosseguimento ao
                              agendamento e acompanhe a confirmação no Portal do
                              Paciente.
                            </Typography>
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: "neutral100.main",
                                border: "1px solid",
                                borderColor: "divider",
                                wordBreak: "break-all",
                              }}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {advancePaymentLink}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <Button
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(
                                      advancePaymentLink,
                                    );
                                    toast.success("Link copiado!");
                                  } catch {
                                    toast.error(
                                      "Não foi possível copiar o link.",
                                    );
                                  }
                                }}
                                sx={{ textTransform: "none" }}
                              >
                                Copiar link
                              </Button>
                              <Button
                                variant="contained"
                                endIcon={<OpenInNewIcon />}
                                onClick={() =>
                                  window.open(advancePaymentLink, "_blank")
                                }
                                sx={{ textTransform: "none" }}
                              >
                                Abrir pagamento
                              </Button>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                  </Stack>
                )}

              {/* Botões de ação */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ width: "100%", mt: 2 }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setShowSuccessDialog(false)}
                  fullWidth
                  sx={{
                    borderColor: "primary.main",
                    color: "primary.main",
                    "&:hover": {
                      borderColor: "primary.dark",
                      bgcolor: "primary",
                    },
                  }}
                >
                  Fechar
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  endIcon={<OpenInNewIcon />}
                  onClick={() =>
                    window.open(getPatientPortalUrls().signin, "_blank")
                  }
                  sx={{ textTransform: "none" }}
                >
                  Acessar Portal do Paciente
                </Button>
              </Stack>

              <Alert severity="info" sx={{ width: "100%" }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Acesso ao Portal do Paciente
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use seu <strong>CPF</strong> e a <strong>senha</strong>{" "}
                  cadastrados. Se for seu primeiro acesso, faça o cadastro. Se
                  não lembrar a senha, recupere por lá.
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ mt: 1.5 }}
                >
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() =>
                      window.open(getPatientPortalUrls().recover, "_blank")
                    }
                    sx={{ textTransform: "none" }}
                  >
                    Recuperar senha
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() =>
                      window.open(getPatientPortalUrls().signup, "_blank")
                    }
                    sx={{ textTransform: "none" }}
                  >
                    Primeiro acesso
                  </Button>
                </Stack>
              </Alert>
            </Stack>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog de coleta de endereço — exibido APÓS autenticação quando serviceModel=domicilioService */}
      {domicilioAddressDialogOpen && (
        <Dialog
          open={domicilioAddressDialogOpen}
          onClose={() => {
            setDomicilioAddressDialogOpen(false);
            // Limpa seleção para reiniciar o fluxo
            setSelectedSlot(null);
            setSelectedDay(null);
            domicilioSlotRef.current = null;
            domicilioScheduleRef.current = null;
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Endereço para Atendimento a Domicílio</DialogTitle>
          <DialogContent>
            <DomicilioAddressStep
              control={addressControl}
              setValue={addressSetValue}
              trigger={addressTrigger}
              onBack={() => {
                setDomicilioAddressDialogOpen(false);
                setSelectedSlot(null);
                setSelectedDay(null);
                domicilioSlotRef.current = null;
                domicilioScheduleRef.current = null;
              }}
              onNext={async () => {
                // Fecha o dialog de endereço e cria o Appointment com o endereço coletado
                setDomicilioAddressDialogOpen(false);
                await createPublicAppointment();
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog de autenticação para agendamento */}

      {authDialogOpen && (
        <AppointmentAuthDialog
          open={authDialogOpen}
          onClose={() => {
            setAuthDialogOpen(false);
            setSelectedSlot(null); // Limpa o slot selecionado ao fechar o dialog
            setSelectedDay(null); // Limpa o dia selecionado ao fechar o dialog
          }}
          onSuccess={async () => {
            setAuthDialogOpen(false);
            // Para agendamento a domicílio: coleta o endereço antes de criar o Appointment.
            // O CPF já está disponível no localStorage após a autenticação.
            if (serviceModel === "domicilioService") {
              // Salva slot e schedule em refs ANTES do onClose() limpar os estados
              domicilioSlotRef.current = selectedSlot;
              domicilioScheduleRef.current = selectedSchedule;
              const cpfAuthed = (
                localStorage.getItem("username") || ""
              ).replace(/\D/g, "");
              addressReset({ cpf: cpfAuthed, domicilioAddress: undefined });
              setDomicilioAddressDialogOpen(true);
            } else {
              await createPublicAppointment();
            }
          }}
          onVetPetSelected={(petId) => {
            // No app veterinário (defaultApp=false), o usuário escolhe o pet antes de confirmar.
            // Salvamos em ref para uso imediato na criação do Appointment.
            selectedVetPetRef.current = { id: petId };
          }}
          slotInfo={
            selectedSlot && selectedSchedule
              ? (() => {
                  // Monta a data
                  let date = "";
                  // Se não apresenta horários, usa o selectedDay
                  if (!practitioner.schedulePresentation && selectedDay) {
                    date = selectedDay.toLocaleDateString("pt-BR", {
                      weekday: "long",
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    });
                  } else {
                    // Se apresenta horários, busca o dia do slot
                    const slotDay = schedule.find((day) =>
                      day.slots.some((s) => s.id === selectedSlot.id),
                    );
                    date = slotDay
                      ? slotDay.date.toLocaleDateString("pt-BR", {
                          weekday: "long",
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })
                      : "";
                  }

                  // Monta o objeto de forma condicional
                  const info: any = {
                    date,
                    practitionerName: practitioner.name,
                    serviceCost:
                      getScheduleExtensions(selectedSchedule).serviceCost,
                  };

                  // Só adiciona time se schedulePresentation estiver habilitado
                  if (practitioner.schedulePresentation !== false) {
                    info.time = selectedSlot.time;
                  }

                  return info;
                })()
              : undefined
          }
        />
      )}
    </Stack>
  );
}

/**
 * @component LoadingState
 * @description Estado de carregamento com skeletons
 */
function LoadingState({ columns }: { columns: number }) {
  const gridSize = 12 / Math.min(Math.max(columns, 1), 4);

  return (
    <Grid container spacing={3} width="100%">
      {Array.from({ length: 6 }).map((_, index) => (
        <Grid item xs={12} sm={gridSize} key={index}>
          <Card sx={{ borderRadius: 3 }}>
            <Skeleton variant="rectangular" height={220} animation="wave" />
            <CardContent>
              <Skeleton variant="text" width="60%" height={28} />
              <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
              <Skeleton
                variant="text"
                width="100%"
                height={60}
                sx={{ mt: 1 }}
              />
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                sx={{ mt: 2 }}
              >
                <Skeleton variant="circular" width={36} height={36} />
                <Skeleton variant="circular" width={36} height={36} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

/**
 * @component EmptyState
 * @description Estado quando não há profissionais
 */
interface EmptyStateProps {
  onClearFilters?: () => void;
}

function EmptyState({ onClearFilters }: EmptyStateProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 2,
      }}
    >
      <Typography
        variant="h6"
        color="text.secondary"
        textAlign="center"
        gutterBottom
      >
        Nenhum profissional encontrado
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        sx={{ mb: 3 }}
      >
        Verifique os parâmetros de busca
      </Typography>
      {onClearFilters && (
        <Button
          variant="outlined"
          color="primary"
          onClick={onClearFilters}
          sx={{ mt: 2 }}
        >
          Limpar filtros e voltar
        </Button>
      )}
    </Box>
  );
}

/**
 * @component ErrorState
 * @description Estado de erro
 */
function ErrorState({ message }: { message: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 2,
      }}
    >
      <Typography variant="h6" color="error" textAlign="center" gutterBottom>
        Erro ao carregar profissionais
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {message}
      </Typography>
    </Box>
  );
}

/**
 * @component PractitionerCardsContent
 * @description Conteúdo principal dos cards
 */
function PractitionerCardsContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { formatDateToISO } = useDatetime;

  // Parâmetros da URL para customização via iframe
  const columns = parseInt(searchParams.get("columns") || "3", 10);
  const itemsPerPage = parseInt(searchParams.get("itemsPerPage") || "6", 10);
  const organization = searchParams.get("organization") || undefined;
  const cardSizeParam = searchParams.get("cardSize") || "medium";

  // Parâmetro para controlar exibição dos filtros
  // Se não especificado, assume true (exibe filtros por padrão)
  const showFiltersParam = searchParams.get("showFilters");
  const showFilters =
    showFiltersParam === null ? true : showFiltersParam === "true";

  // Parâmetros de filtro da URL
  const locationParam = searchParams.get("location") || undefined;
  const serviceModelParam = searchParams.get("serviceModel") || undefined;
  const specialtyParam = searchParams.get("specialty") || undefined;
  const stateParam = searchParams.get("state") || undefined;

  // Pagamento antecipado: quando true, o fluxo público pode disparar cobrança automática
  // após a reserva (somente para agendas com custo).
  const advancePaymentParam = searchParams.get("advancePayment");
  const advancePayment =
    advancePaymentParam === null ? false : advancePaymentParam === "true";

  // Valida o parâmetro cardSize para garantir que seja um valor válido
  const cardSize = ["small", "medium", "large"].includes(cardSizeParam)
    ? (cardSizeParam as "small" | "medium" | "large")
    : "medium";

  // Configuração do formulário de filtros
  const defaultValues: IFormInput = {
    // Usa SERVICE_MODEL_LABELS para mapear todos os 4 tipos de atendimento
    serviceModel: serviceModelParam
      ? {
          label: SERVICE_MODEL_LABELS[serviceModelParam] ?? "Presencial",
          value: serviceModelParam,
        }
      : null,
    state: stateParam
      ? {
          label: stateParam,
          value: stateParam,
        }
      : null,
    date: dayjs(),
    // Não inicializa com especialidade da URL para evitar seleção automática
    // A especialidade deve ser selecionada manualmente pelo usuário
    healthcareService: null,
    organization: organization
      ? {
          label: organization,
          value: organization,
        }
      : null,
    practitioner: null,
  };

  const methods = useForm<IFormInput>({ defaultValues });
  const { handleSubmit, control, watch, resetField, setValue, getValues } =
    methods;

  const [page, setPage] = useState(1);
  const [selectedPractitioner, setSelectedPractitioner] =
    useState<Practitioner | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Estado para armazenar schedules pré-carregados
  const [preloadedSchedules, setPreloadedSchedules] = useState<
    Map<string, fhir5.Schedule[]>
  >(new Map());
  // Estado para armazenar Locations pré-carregadas (scheduleId -> Location)
  const [preloadedLocations, setPreloadedLocations] = useState<
    Map<string, fhir5.Location>
  >(new Map());
  const [loadingSchedules, setLoadingSchedules] = useState(false);

  // Deriva informações de disponibilidade para decisões de auto-seleção e UI
  // a partir das Locations pré-carregadas (scheduleId -> Location).
  // Agora detecta os 4 tipos: virtual, chat, domicílio e presencial.
  const scheduleAvailability = useMemo(() => {
    let hasVirtual = false;
    let hasChat = false;
    let hasDomicilio = false;
    let hasPhysical = false;
    const physicalStates = new Set<string>();

    preloadedLocations.forEach((location) => {
      const channel = getAttendanceChannelFromLocation(location);
      const isVirtual = !!location?.extension?.find(
        (ext) =>
          ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service" &&
          ext.valueBoolean === true,
      );

      if (channel === "chat") {
        hasChat = true;
        return;
      }
      if (channel === "domicilio") {
        hasDomicilio = true;
        return;
      }
      if (isVirtual) {
        hasVirtual = true;
        return;
      }

      // Somente conta como presencial se não for nenhum dos tipos especiais
      hasPhysical = true;
      const state = location.address?.state;
      if (state) physicalStates.add(state);
    });

    const typeCount = [hasVirtual, hasChat, hasDomicilio, hasPhysical].filter(
      Boolean,
    ).length;
    const onlyVirtual = hasVirtual && typeCount === 1;
    const onlyChat = hasChat && typeCount === 1;
    const onlyDomicilio = hasDomicilio && typeCount === 1;
    const onlyPhysical = hasPhysical && typeCount === 1;
    const onlyPhysicalState =
      physicalStates.size === 1 ? Array.from(physicalStates)[0] : undefined;

    return {
      hasVirtual,
      hasChat,
      hasDomicilio,
      hasPhysical,
      onlyVirtual,
      onlyChat,
      onlyDomicilio,
      onlyPhysical,
      physicalStates,
      onlyPhysicalState,
    };
  }, [preloadedLocations]);

  // Extrai estados únicos das locations presenciais pré-carregadas.
  // Exclui virtual, chat e domicílio — apenas localizações físicas têm UF relevante.
  const availableStates = useMemo(() => {
    if (preloadedLocations.size === 0) return undefined;

    const statesSet = new Set<string>();
    preloadedLocations.forEach((location) => {
      // Considera apenas locais que correspondem a physicalLocation
      if (locationMatchesModel(location, "physicalLocation")) {
        const state = location.address?.state;
        if (state) statesSet.add(state);
      }
    });
    return statesSet.size > 0 ? Array.from(statesSet) : undefined;
  }, [preloadedLocations]);

  // Busca schedules previamente quando houver organization na URL
  // Fluxo: chamada GetSchedule usando apenas organization (sem filtro por IDs específicos)
  useEffect(() => {
    async function fetchPreloadedSchedules() {
      if (!organization) {
        setPreloadedSchedules(new Map());
        setPreloadedLocations(new Map());
        return;
      }

      setLoadingSchedules(true);
      try {
        const schedulesMap = new Map<string, fhir5.Schedule[]>();
        const locationsByIdMap = new Map<string, fhir5.Location>();
        const scheduleLocationsMap = new Map<string, fhir5.Location>();

        // Parâmetros: buscamos agendas ativas da organização com includeLocation
        const scheduleParams: Parameters<typeof GetSchedule>[0] = {
          includeLocation: true,
          active: true,
          count: 250,
        };
        if (organization) {
          scheduleParams.organization = organization;
        }

        const scheduleResponse = await GetSchedule(scheduleParams);

        if (scheduleResponse?.entry) {
          // Extrai Locations da resposta (entries com search.mode === "include")
          scheduleResponse.entry.forEach((entry: any) => {
            if (
              entry?.resource?.resourceType === "Location" &&
              entry?.search?.mode === "include"
            ) {
              const location = entry.resource as fhir5.Location;
              if (location.id) {
                locationsByIdMap.set(location.id, location);
              }
            }
          });

          const schedules = scheduleResponse.entry
            .filter(
              (entry: any) =>
                entry?.resource?.resourceType === "Schedule" &&
                entry?.search?.mode === "match",
            )
            .map((entry: any) => entry.resource as fhir5.Schedule);

          // Agrupa schedules por practitioner e mapeia Locations por scheduleId
          schedules.forEach((schedule: fhir5.Schedule) => {
            const practitionerActor = schedule.actor?.find(
              (actor: fhir5.Reference) =>
                actor.reference?.startsWith("Practitioner/"),
            );
            if (practitionerActor?.reference) {
              const practitionerId = practitionerActor.reference.split("/")[1];
              if (practitionerId) {
                const existingSchedules =
                  schedulesMap.get(practitionerId) || [];
                schedulesMap.set(practitionerId, [
                  ...existingSchedules,
                  schedule,
                ]);
              }
            }

            // Mapeia Location para este schedule
            const locationActor = schedule.actor?.find(
              (actor: fhir5.Reference) =>
                actor.reference?.startsWith("Location/"),
            );
            if (locationActor?.reference && schedule.id) {
              const locationId = locationActor.reference.split("/")[1];
              const location = locationsByIdMap.get(locationId);
              if (location) {
                scheduleLocationsMap.set(schedule.id, location);
              }
            }
          });
        }

        setPreloadedSchedules(schedulesMap);
        setPreloadedLocations(scheduleLocationsMap);

        // Auto-seleção de filtros quando houver apenas um tipo de agenda na organização.
        // Regras:
        // - Não sobrescreve parâmetros já definidos na URL.
        // - Usa Locations pré-carregadas para decidir o tipo único, se houver.
        if (!serviceModelParam && scheduleLocationsMap.size > 0) {
          let hasVirtual = false;
          let hasChat = false;
          let hasDomicilio = false;
          let hasPhysical = false;
          const physicalStates = new Set<string>();

          scheduleLocationsMap.forEach((location) => {
            const channel = getAttendanceChannelFromLocation(location);
            const isVirtual = !!location?.extension?.find(
              (ext) =>
                ext.url ===
                  "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service" &&
                ext.valueBoolean === true,
            );

            if (channel === "chat") {
              hasChat = true;
              return;
            }
            if (channel === "domicilio") {
              hasDomicilio = true;
              return;
            }
            if (isVirtual) {
              hasVirtual = true;
              return;
            }

            hasPhysical = true;
            const state = location.address?.state;
            if (state) physicalStates.add(state);
          });

          const typeCount = [
            hasVirtual,
            hasChat,
            hasDomicilio,
            hasPhysical,
          ].filter(Boolean).length;

          // Auto-seleciona apenas quando há exatamente 1 tipo de agenda
          if (typeCount === 1) {
            const selectedServiceModel = hasVirtual
              ? "virtualService"
              : hasChat
                ? "chatService"
                : hasDomicilio
                  ? "domicilioService"
                  : "physicalLocation";

            const newParams = new URLSearchParams(searchParams);
            newParams.set("serviceModel", selectedServiceModel);

            // Mantém o formulário sincronizado com a URL
            setValue("serviceModel", {
              label: SERVICE_MODEL_LABELS[selectedServiceModel],
              value: selectedServiceModel,
            });

            // Se for presencial e houver apenas 1 UF, já seleciona automaticamente
            if (
              selectedServiceModel === "physicalLocation" &&
              !stateParam &&
              physicalStates.size === 1
            ) {
              const onlyState = Array.from(physicalStates)[0];
              newParams.set("state", onlyState);
              setValue("state", { label: onlyState, value: onlyState });
            }

            setSearchParams(newParams, { replace: true });
          }
        }
      } catch (error) {
        console.error("Erro ao buscar schedules pré-carregados:", error);
        setPreloadedSchedules(new Map());
        setPreloadedLocations(new Map());
      } finally {
        setLoadingSchedules(false);
      }
    }

    fetchPreloadedSchedules();
  }, [organization]);

  // Se o usuário selecionou (ou veio pré-selecionado) como presencial e só existe 1 UF,
  // garante que o filtro de UF acompanhe automaticamente (sem exigir interação).
  useEffect(() => {
    const isPhysical = serviceModelParam === "physicalLocation";
    if (!isPhysical) return;
    if (stateParam) return;
    if (!scheduleAvailability.onlyPhysicalState) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.set("state", scheduleAvailability.onlyPhysicalState);
    setSearchParams(newParams, { replace: true });
    setValue("state", {
      label: scheduleAvailability.onlyPhysicalState,
      value: scheduleAvailability.onlyPhysicalState,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    serviceModelParam,
    stateParam,
    scheduleAvailability.onlyPhysicalState,
    setSearchParams,
  ]);

  // Função para atualizar os parâmetros da URL quando os filtros mudam
  const updateUrlParams = (filters: IFormInput) => {
    const newParams = new URLSearchParams(searchParams);

    // Atualiza ou remove parâmetros de filtro
    if (filters.serviceModel?.value) {
      newParams.set("serviceModel", filters.serviceModel.value);
    } else {
      newParams.delete("serviceModel");
    }

    if (filters.state?.value) {
      newParams.set("state", filters.state.value);
    } else {
      newParams.delete("state");
    }

    if (filters.healthcareService?.label) {
      newParams.set("specialty", filters.healthcareService.label);
    } else {
      newParams.delete("specialty");
    }

    // Atualiza a URL sem recarregar a página
    setSearchParams(newParams, { replace: true });
  };

  // Função de busca quando os filtros são aplicados
  const handleSearch = async (data: IFormInput) => {
    setIsSearching(true);
    updateUrlParams(data);
    // A busca será feita automaticamente quando os parâmetros da URL mudarem
    // através do useEffect no usePractitionerCards
    setTimeout(() => setIsSearching(false), 500);
  };

  // Função para limpar todos os filtros e voltar ao estado inicial
  const handleClearFilters = () => {
    // Limpa todos os campos do formulário
    resetField("serviceModel");
    resetField("state");
    resetField("healthcareService");
    resetField("organization");
    resetField("practitioner");

    // Remove todos os parâmetros de filtro da URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("serviceModel");
    newParams.delete("state");
    newParams.delete("specialty");
    newParams.delete("location");

    // Mantém apenas os parâmetros de configuração (columns, itemsPerPage, cardSize, organization)
    setSearchParams(newParams, { replace: true });

    // Reseta a página para a primeira
    setPage(1);
  };

  // Observa quando o modelo de atendimento é removido e reseta os profissionais
  useEffect(() => {
    // Se não há modelo de atendimento selecionado, limpa todos os filtros da URL
    if (!serviceModelParam) {
      // Verifica se há parâmetros de filtro para limpar
      const hasFilterParams = stateParam || specialtyParam || locationParam;

      if (hasFilterParams) {
        const newParams = new URLSearchParams(searchParams);

        // Remove todos os parâmetros de filtro relacionados a profissionais
        newParams.delete("serviceModel");
        newParams.delete("state");
        newParams.delete("specialty");
        newParams.delete("location");

        // Atualiza a URL
        setSearchParams(newParams, { replace: true });
      }

      // Limpa os campos do formulário
      resetField("state");
      resetField("healthcareService");
      resetField("organization");
      resetField("practitioner");

      // Reseta a página
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceModelParam]);

  // Busca dados reais da API com filtros (fluxo original mantido)
  const { practitioners, loading, error } = usePractitionerCards({
    organization,
    // Só aplica filtros se showFilters for true ou se houver filtros na URL
    location: showFilters ? locationParam : undefined,
    serviceModel: showFilters ? serviceModelParam : undefined,
    specialty: showFilters ? specialtyParam : undefined,
    state: showFilters ? stateParam : undefined,
  });

  // Lista de profissionais considerada para exibição.
  // Como não utilizamos mais o parâmetro de schedules na URL,
  // trabalhamos sempre com a lista completa retornada pelo hook.
  const filteredPractitioners = useMemo(() => practitioners, [practitioners]);

  // Paginação
  const totalPages = Math.ceil(filteredPractitioners.length / itemsPerPage);
  const currentPractitioners = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredPractitioners.slice(start, start + itemsPerPage);
  }, [filteredPractitioners, page, itemsPerPage]);

  // Calcula o tamanho do grid baseado nas colunas
  const gridSize = 12 / Math.min(Math.max(columns, 1), 4);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSelectPractitioner = (practitioner: Practitioner) => {
    setSelectedPractitioner(practitioner);
  };

  const handleBackToCards = () => {
    setSelectedPractitioner(null);
  };

  // Se há um profissional selecionado, exibe a tela de horários
  if (selectedPractitioner) {
    // Busca schedules pré-carregados para este practitioner
    const practitionerSchedules =
      preloadedSchedules.get(selectedPractitioner.id) || undefined;

    return (
      <PractitionerSchedule
        practitioner={selectedPractitioner}
        onBack={handleBackToCards}
        preloadedSchedules={practitionerSchedules}
        preloadedLocations={preloadedLocations}
        serviceModel={serviceModelParam}
        advancePayment={advancePayment}
      />
    );
  }

  // Verifica se há filtros selecionados
  // Se showFilters for false, exibe todos os profissionais diretamente
  // Caso contrário, exibe cards após modelo ser selecionado (especialidade é opcional)
  const hasFilters = !!(
    serviceModelParam ||
    stateParam ||
    specialtyParam ||
    locationParam
  );

  // Exibe cards se:
  // - showFilters for false (exibe todos diretamente)
  // - ou se houver seleção mínima completa:
  //   - tele: apenas serviceModel
  //   - presencial: serviceModel + state
  const shouldShowCards =
    !showFilters ||
    (!!serviceModelParam &&
      (serviceModelParam !== "physicalLocation" || !!stateParam));

  // Define a largura máxima do container baseado no tamanho do card e número de colunas
  const containerMaxWidths = {
    small: columns === 1 ? 320 : 1000,
    medium: columns === 1 ? 400 : 1200,
    large: columns === 1 ? 480 : 1400,
  };

  return (
    <Stack spacing={4} width="100%" alignItems="center">
      {/* Componente de filtros seguindo o padrão do SchedulingSearch */}
      {/* Só exibe os filtros se o parâmetro showFilters for true */}
      {showFilters && (
        <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto" }}>
          <SearchFields
            resetField={resetField}
            onSubmit={handleSearch}
            control={control}
            watch={watch}
            handleSubmit={handleSubmit}
            availableSpecialties={[]}
            setValue={setValue}
            isSearching={isSearching || loading}
            onModelCleared={handleClearFilters}
            hiddenFilters={{
              date: true, // Oculta o filtro de data
              practitioner: true, // Oculta o filtro de profissional
              clinic: true, // Oculta o filtro de clínica
              // Trava o seletor de modelo quando há apenas 1 tipo disponível
              serviceModel:
                scheduleAvailability.onlyVirtual ||
                scheduleAvailability.onlyChat ||
                scheduleAvailability.onlyDomicilio ||
                scheduleAvailability.onlyPhysical,
              // Oculta seletor de estado quando não é presencial ou há só 1 UF
              state:
                serviceModelParam !== "physicalLocation" ||
                scheduleAvailability.physicalStates.size === 1,
            }}
            availableStates={availableStates}
          />
        </Box>
      )}

      {/* Exibe os cards se:
          - showFilters for false (exibe todos os profissionais diretamente)
          - ou se houver filtros selecionados (quando showFilters for true) */}
      {shouldShowCards && (
        <>
          {loading ? (
            <LoadingState columns={columns} />
          ) : error ? (
            <ErrorState message={error.message} />
          ) : !practitioners.length ? (
            <EmptyState onClearFilters={handleClearFilters} />
          ) : (
            <>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: containerMaxWidths[cardSize],
                  mx: "auto",
                }}
              >
                <Grid container spacing={2} justifyContent="center">
                  {currentPractitioners.map((practitioner) => (
                    <Grid
                      item
                      xs={12}
                      sm={isMobileScreen ? 12 : gridSize}
                      md={gridSize}
                      key={practitioner.id}
                    >
                      <PractitionerCard
                        practitioner={practitioner}
                        onSelect={handleSelectPractitioner}
                        cardSize={cardSize}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobileScreen ? "small" : "medium"}
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontWeight: 500,
                    },
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </Stack>
  );
}

/**
 * @component PractitionerCards
 * @description Componente principal - página de cards de profissionais
 * @remarks Projetado para ser embarcado via iframe em sistemas externos
 *
 * Parâmetros de URL suportados:
 * - columns: número de colunas (1-4, padrão: 3)
 * - itemsPerPage: itens por página (padrão: 6)
 * - organization: ID da organização para filtrar profissionais
 * - schedules: IDs de schedules separados por vírgula (fluxo unificado com organization)
 * - cardSize: tamanho dos cards - "small", "medium" ou "large" (padrão: "medium")
 * - showFilters: exibir filtros de busca - "true" ou "false" (padrão: "true")
 * - location: ID da localização para filtrar profissionais
 * - serviceModel: modelo de atendimento - "virtualService" ou "physicalLocation"
 * - specialty: nome da especialidade (HealthcareService display name)
 * - state: sigla do estado (UF) para filtro de localização presencial
 */
export function PractitionerCards() {
  return (
    <>
      {isMobile ? (
        <Stack
          alignItems="center"
          justifyContent="flex-start"
          width="100%"
          height="100%"
          padding="40px 16px"
          sx={{ backgroundColor: "neutral0.main" }}
        >
          <PractitionerCardsContent />
        </Stack>
      ) : (
        <Box
          sx={{
            width: "100%",
            maxHeight: "fit-content",
            overflow: "hidden",
            p: 3,
            backgroundColor: "neutral0.main",
            minHeight: "100vh",
          }}
        >
          <PractitionerCardsContent />
        </Box>
      )}
    </>
  );
}
